import React, { useEffect, useMemo, useState, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { LogOut, MousePointerClick, Users, TrendingUp, TrendingDown, ArrowUp, ArrowDown, Globe, Target, Percent, CalendarDays, Save, RefreshCw, ArrowLeft, Copy, Check, Mail, Send, Loader2, AlertCircle, CheckCircle2, Bell, ThumbsUp, ThumbsDown, X, Filter, Download, FileSpreadsheet, Trash2, Lock, Undo2, Clock, Plus, Pencil, Eye, EyeOff, GripVertical, Upload, Instagram, BarChart3, Building2, UtensilsCrossed, LayoutGrid, Newspaper, Megaphone, FileText, ShieldCheck, Search, Move, Palette, Monitor, ChevronLeft, ChevronRight, Link2, QrCode, ExternalLink } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend, Area, AreaChart } from 'recharts';
import * as XLSX from 'xlsx';
import { api, resolveImageUrl, API } from '../lib/api';
import { LINKS } from '../data/links';
import { LINEUP_SHAPES, lineupShapeIndex } from '../data/lineupShapes';
import FloorplanTab from '../components/FloorplanTab';
import { OnbBar, OnbCell } from '../components/OnboardingAdmin';
import { loadOnb } from '../lib/onboarding';
import { Share2 as Share2Icon, Plus as PlusIcon, Trash2 as TrashIcon, Pipette as PipetteIcon, Map as MapIcon } from 'lucide-react';

import HomeCommand from '../components/HomeCommand';
import MobileNav, { MobileHero, MobileSubTabs, MobileCollapse, isLightColor } from '../components/MobileNav';
import AutomationTab from '../components/AutomationTab';
import { BellRing as BellRingIcon } from 'lucide-react';
const DASH_THEMES = {
  talesverse: { label: 'TalesVerse', accent: '#E63329', accent2: '#F5C518', bg: '#0F0A0A', surface: '#160F0F' },
  midnight: { label: 'Midnight', accent: '#6366F1', accent2: '#22D3EE', bg: '#0B1020', surface: '#141A2E' },
  emerald: { label: 'Emerald', accent: '#10B981', accent2: '#A3E635', bg: '#07140F', surface: '#102018' },
  grape: { label: 'Grape', accent: '#A855F7', accent2: '#EC4899', bg: '#130A1C', surface: '#1E1030' },
  ember: { label: 'Ember', accent: '#F97316', accent2: '#FACC15', bg: '#140B06', surface: '#20130B' },
  ocean: { label: 'Ocean', accent: '#0EA5E9', accent2: '#2DD4BF', bg: '#06121A', surface: '#0E1F2B' },
  clean: { label: 'Clean Merah', accent: '#D92D20', accent2: '#F5A524', bg: '#F4F5F7', surface: '#FFFFFF' },
  cleanorange: { label: 'Clean Oranye', accent: '#C2410C', accent2: '#F59E0B', bg: '#F4F5F7', surface: '#FFFFFF' },
  cleanblue: { label: 'Clean Biru', accent: '#1D4ED8', accent2: '#0EA5E9', bg: '#F4F5F7', surface: '#FFFFFF' },
};

const DASH_BG_DEFAULT = { image_url: '', overlay: 55, blur: 0, fit: 'cover' };

function AppearancePanel({ theme, setTheme, containerSize, setContainerSize, onClose, dashBg, setDashBg, onBgUpload, bgUploading }) {
  return (
    <div className="absolute right-0 top-12 w-72 tv-card p-4 z-50 shadow-2xl max-h-[calc(100vh-90px)] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="appearance-panel">
      <div className="flex items-center justify-between mb-3">
        <div className="font-display text-base inline-flex items-center gap-2"><Palette className="w-4 h-4 text-[#F5C518]" /> Tampilan</div>
        <button onClick={onClose} className="p-1 hover:bg-white/10 rounded"><X className="w-4 h-4" /></button>
      </div>

      <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-2">Tema</div>
      <div className="grid grid-cols-3 gap-2 mb-4">
        {Object.entries(DASH_THEMES).map(([key, t]) => (
          <button key={key} data-testid={`theme-${key}`} onClick={() => setTheme({ ...t, key })}
            className={`rounded-xl p-2 border transition-all ${theme.key === key ? 'border-white/60 scale-[1.02]' : 'border-white/10 hover:border-white/30'}`}
            style={{ background: t.surface }}>
            <div className="flex gap-1 mb-1.5">
              <span className="w-4 h-4 rounded-full" style={{ background: t.accent }} />
              <span className="w-4 h-4 rounded-full" style={{ background: t.accent2 }} />
            </div>
            <div className="text-[10px] font-semibold text-white/80 text-left">{t.label}</div>
          </button>
        ))}
      </div>

      <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-2">Warna Aksen (bebas)</div>
      <div className="flex items-center gap-2 mb-4">
        <input data-testid="accent-picker" type="color" value={theme.accent} onChange={(e) => setTheme({ ...theme, accent: e.target.value, key: 'custom' })}
          className="w-10 h-10 rounded-lg border border-white/20 bg-transparent cursor-pointer" title="Aksen 1" />
        <input data-testid="accent2-picker" type="color" value={theme.accent2} onChange={(e) => setTheme({ ...theme, accent2: e.target.value, key: 'custom' })}
          className="w-10 h-10 rounded-lg border border-white/20 bg-transparent cursor-pointer" title="Aksen 2" />
        <span className="text-xs text-white/50 font-mono">{theme.accent} / {theme.accent2}</span>
      </div>

      <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-2">Warna Background (bebas)</div>
      <div className="flex items-center gap-2 mb-2">
        <input data-testid="bg-picker" type="color" value={theme.bg} onChange={(e) => setTheme({ ...theme, bg: e.target.value, key: 'custom' })}
          className="w-10 h-10 rounded-lg border border-white/20 bg-transparent cursor-pointer" title="Background utama" />
        <input data-testid="surface-picker" type="color" value={theme.surface} onChange={(e) => setTheme({ ...theme, surface: e.target.value, key: 'custom' })}
          className="w-10 h-10 rounded-lg border border-white/20 bg-transparent cursor-pointer" title="Background kartu" />
        <span className="text-xs text-white/50 font-mono">{theme.bg} / {theme.surface}</span>
      </div>
      <div className="flex items-center gap-2 mb-4">
        <input data-testid="bg-hex-input" value={theme.bg} onChange={(e) => { const v = e.target.value.trim(); if (/^#?[0-9a-fA-F]{0,6}$/.test(v)) setTheme({ ...theme, bg: v.startsWith('#') ? v : '#' + v, key: 'custom' }); }}
          placeholder="#0F0A0A" maxLength={7}
          className="flex-1 bg-black/40 border border-white/15 rounded-lg px-3 py-1.5 text-xs text-white font-mono focus:outline-none" />
        <span className="text-[10px] text-white/40">background hex</span>
      </div>

      <div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-2">Gambar Background</div>
<div className="mb-4" data-testid="dash-bg-section">
  <div className="relative h-24 rounded-lg border border-white/15 overflow-hidden mb-2 grid place-items-center" style={{ background: dashBg.image_url ? `url(${resolveImageUrl(dashBg.image_url)}) center/cover no-repeat` : 'rgba(0,0,0,0.3)' }}>
    {!dashBg.image_url && <span className="text-[11px] text-white/40">Belum ada gambar</span>}
    {bgUploading && <div className="absolute inset-0 bg-black/60 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin" /></div>}
  </div>
  <div className="flex gap-2 mb-3">
    <label className="flex-1 text-[11px] font-bold py-2 rounded-lg border border-white/10 bg-white/5 hover:bg-white/10 text-white/80 inline-flex items-center justify-center gap-1 cursor-pointer" data-testid="dash-bg-upload">
      <Upload className="w-3 h-3" />{dashBg.image_url ? 'Ganti Gambar' : 'Upload Gambar'}
      <input type="file" accept="image/png,image/jpeg,image/webp,image/gif" className="hidden" onChange={(e) => { const f = e.target.files && e.target.files[0]; e.target.value = ''; if (f) onBgUpload(f); }} />
    </label>
    {dashBg.image_url && <button type="button" onClick={() => setDashBg({ ...dashBg, image_url: '' })} data-testid="dash-bg-remove"
      className="px-3 text-[11px] font-bold rounded-lg border border-white/10 bg-white/5 hover:bg-red-500/20 text-white/80 inline-flex items-center gap-1"><Trash2 className="w-3 h-3" />Hapus</button>}
  </div>
  {dashBg.image_url && (<>
    <div className="text-[11px] text-white/60 mb-1">Gelapkan: {dashBg.overlay}%</div>
    <input type="range" min="0" max="90" value={dashBg.overlay} onChange={(e) => setDashBg({ ...dashBg, overlay: Number(e.target.value) })} className="w-full mb-2" style={{ accentColor: theme.accent }} data-testid="dash-bg-overlay" />
    <div className="text-[11px] text-white/60 mb-1">Blur: {dashBg.blur}px</div>
    <input type="range" min="0" max="20" value={dashBg.blur} onChange={(e) => setDashBg({ ...dashBg, blur: Number(e.target.value) })} className="w-full mb-2" style={{ accentColor: theme.accent }} data-testid="dash-bg-blur" />
    <div className="grid grid-cols-3 gap-2">
      {[['cover', 'Penuh'], ['contain', 'Utuh'], ['repeat', 'Ulang']].map(([k, l]) => (
        <button key={k} type="button" onClick={() => setDashBg({ ...dashBg, fit: k })} data-testid={`dash-bg-fit-${k}`}
          className={`text-[11px] font-bold py-1.5 rounded-lg border transition-colors ${dashBg.fit === k ? 'bg-[#E63329] text-white border-transparent' : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'}`}>{l}</button>
      ))}
    </div>
  </>)}
</div>

<div className="text-[10px] uppercase tracking-widest text-white/40 font-bold mb-2">Lebar Konten</div>
      <div className="grid grid-cols-3 gap-2">
        {[['compact', 'Compact'], ['normal', 'Normal'], ['full', 'Full']].map(([key, lbl]) => (
          <button key={key} data-testid={`width-${key}`} onClick={() => setContainerSize(key)}
            className={`text-[11px] font-bold py-2 rounded-lg border transition-colors inline-flex items-center justify-center gap-1 ${containerSize === key ? 'bg-[#E63329] text-white border-transparent' : 'bg-white/5 text-white/70 border-white/10 hover:bg-white/10'}`}>
            <Monitor className="w-3 h-3" />{lbl}
          </button>
        ))}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const [analytics, setAnalytics] = useState(null);
  const [regs, setRegs] = useState([]);
  const [corporate, setCorporate] = useState([]);
  const [fnb, setFnb] = useState([]);
  const [lineup, setLineup] = useState([]);
  const [mediaPartners, setMediaPartners] = useState([]);
  const [marqueeItems, setMarqueeItems] = useState([]);
  const [proposal, setProposal] = useState(null);
  const [pixels, setPixels] = useState({ meta_pixel_id: '', ga_measurement_id: '', gtm_id: '', tiktok_pixel_id: '' });
  const [emailStatus, setEmailStatus] = useState(null);
  const [ipWhitelist, setIpWhitelist] = useState({ ips: [], current_ip: '' });
  const [tab, setTab] = useState(() => localStorage.getItem('tv_dash_tab') || 'analytics');
  const [savedMsg, setSavedMsg] = useState('');
  const [loading, setLoading] = useState(true);

  const [theme, setTheme] = useState(() => { try { return JSON.parse(localStorage.getItem('tv_dash_theme')) || { ...DASH_THEMES.talesverse, key: 'talesverse' }; } catch (e) { return { ...DASH_THEMES.talesverse, key: 'talesverse' }; } });
  const [containerSize, setContainerSize] = useState(() => localStorage.getItem('tv_dash_width') || 'normal');
  const [appearanceOpen, setAppearanceOpen] = useState(false);
  const [dashBg, setDashBg] = useState(() => { try { return { ...DASH_BG_DEFAULT, ...(JSON.parse(localStorage.getItem('tv_dash_bg')) || {}) }; } catch (e) { return DASH_BG_DEFAULT; } });
  const [bgUploading, setBgUploading] = useState(false);
  const bgLoadedRef = useRef(false);
  useEffect(() => {
    api.get('/config/dashboard-bg')
      .then(({ data }) => { if (data && Object.keys(data).length) setDashBg((p) => ({ ...p, ...data })); })
      .catch(() => {})
      .finally(() => { setTimeout(() => { bgLoadedRef.current = true; }, 300); });
  }, []);
  useEffect(() => {
    try { localStorage.setItem('tv_dash_bg', JSON.stringify(dashBg)); } catch (e) { /* abaikan */ }
    if (!bgLoadedRef.current) return undefined;
    const t = setTimeout(() => { api.put('/config/dashboard-bg', dashBg).catch(() => {}); }, 600);
    return () => clearTimeout(t);
  }, [dashBg]);
  const uploadDashBg = async (file) => {
    setBgUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/dashboard/bg/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      bgLoadedRef.current = true;
      setDashBg((p) => ({ ...p, image_url: data.image_url }));
    } catch (e) {
      window.alert((e.response && e.response.data && e.response.data.detail) || 'Gagal upload gambar.');
    } finally {
      setBgUploading(false);
    }
  };

  useEffect(() => { localStorage.setItem('tv_dash_theme', JSON.stringify(theme)); }, [theme]);
  useEffect(() => { localStorage.setItem('tv_dash_width', containerSize); }, [containerSize]);

  const [online, setOnline] = useState(0);
  const [onlineByPath, setOnlineByPath] = useState([]);
  useEffect(() => {
    let alive = true;
    const poll = async () => { try { const { data } = await api.get('/presence/active'); if (alive) { setOnline(data.online); setOnlineByPath(data.by_path || []); } } catch (e) {} };
    poll();
    const t = setInterval(poll, 10000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  // --- New registration notifications ---
  const [recentRegs, setRecentRegs] = useState([]);
  const [regLastSeen, setRegLastSeen] = useState(() => localStorage.getItem('tv_reg_lastseen') || '');
  const [bellOpen, setBellOpen] = useState(false);
  const [toastReg, setToastReg] = useState(null);
  const [soundOn, setSoundOn] = useState(() => localStorage.getItem('tv_notif_sound') !== 'off');
  const initRef = useRef(false);
  const prevNewRef = useRef(-1);
  const audioRef = useRef(null);

  useEffect(() => { localStorage.setItem('tv_notif_sound', soundOn ? 'on' : 'off'); }, [soundOn]);

  const beep = () => {
    if (!soundOn) return;
    try {
      if (!audioRef.current) audioRef.current = new (window.AudioContext || window.webkitAudioContext)();
      const ctx = audioRef.current;
      if (ctx.state === 'suspended') ctx.resume();
      [880, 1320].forEach((freq, i) => {
        const o = ctx.createOscillator();
        const g = ctx.createGain();
        o.connect(g); g.connect(ctx.destination);
        o.type = 'sine'; o.frequency.value = freq;
        const t0 = ctx.currentTime + i * 0.16;
        g.gain.setValueAtTime(0.0001, t0);
        g.gain.exponentialRampToValueAtTime(0.22, t0 + 0.02);
        g.gain.exponentialRampToValueAtTime(0.0001, t0 + 0.32);
        o.start(t0); o.stop(t0 + 0.34);
      });
    } catch (e) {}
  };

  useEffect(() => {
    let alive = true;
    const poll = async () => {
      try {
        const { data } = await api.get('/registrations/recent', { params: { limit: 25 } });
        if (!alive) return;
        setRecentRegs(data);
        if (!initRef.current && !localStorage.getItem('tv_reg_lastseen')) {
          const base = (data[0] && data[0].submitted_at) || new Date().toISOString();
          localStorage.setItem('tv_reg_lastseen', base);
          setRegLastSeen(base);
        }
        initRef.current = true;
      } catch (e) {}
    };
    poll();
    const t = setInterval(poll, 12000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const newRegs = regLastSeen ? recentRegs.filter((r) => r.submitted_at && r.submitted_at > regLastSeen) : [];
  const newByType = newRegs.reduce((a, r) => { a[r.type] = (a[r.type] || 0) + 1; return a; }, {});

  useEffect(() => {
    if (prevNewRef.current >= 0 && newRegs.length > prevNewRef.current) {
      beep();
      setToastReg(newRegs[0]);
      setTimeout(() => setToastReg((cur) => (cur === newRegs[0] ? null : cur)), 7000);
    }
    prevNewRef.current = newRegs.length;
  }, [newRegs.length]); // eslint-disable-line

  const markRegsRead = () => {
    const base = (recentRegs[0] && recentRegs[0].submitted_at) || new Date().toISOString();
    localStorage.setItem('tv_reg_lastseen', base);
    setRegLastSeen(base);
    setBellOpen(false);
  };
  const REG_LABEL = { creator: 'Creator', fnb: 'F&B', corporate: 'Brand Booth' };
  const REG_NAVKEY = { creator: 'registrations', fnb: 'fnb', corporate: 'corporate' };

  useEffect(() => { localStorage.setItem('tv_dash_tab', tab); }, [tab]);

  const readRange = () => {
    try { return JSON.parse(localStorage.getItem('tv_analytics_range') || '{}'); } catch (e) { return {}; }
  };

  useEffect(() => {
    if (!localStorage.getItem('tv_admin_token')) {
      navigate('/dashboard/login');
      return;
    }
    load();
  }, [navigate]);

  const load = async () => {
    setLoading(true);
    const rg = readRange();
    try {
      const [a, r, corp, c, es, lu, fb, mp, mq, pr, ipw] = await Promise.all([
        api.get('/analytics', { params: { start: rg.start || undefined, end: rg.end || undefined, cmp_start: rg.cmpStart || undefined, cmp_end: rg.cmpEnd || undefined } }),
        api.get('/registrations'),
        api.get('/corporate/registrations').catch(() => ({ data: [] })),
        api.get('/config'),
        api.get('/email/status').catch(() => ({ data: null })),
        api.get('/lineup/all').catch(() => ({ data: [] })),
        api.get('/fnb/registrations').catch(() => ({ data: [] })),
        api.get('/media-partners/all').catch(() => ({ data: [] })),
        api.get('/config/marquee').catch(() => ({ data: { items: [] } })),
        api.get('/proposal').catch(() => ({ data: null })),
        api.get('/ip-whitelist').catch(() => ({ data: { ips: [], current_ip: '' } })),
      ]);
      setAnalytics(a.data);
      setRegs(r.data);
      setCorporate(corp.data);
      setFnb(fb.data);
      setLineup(lu.data);
      setMediaPartners(mp.data);
      setMarqueeItems((mq.data && mq.data.items) || []);
      setProposal(pr.data);
      setIpWhitelist(ipw.data);
      setPixels({
        meta_pixel_id: c.data.meta_pixel_id || '',
        ga_measurement_id: c.data.ga_measurement_id || '',
        gtm_id: c.data.gtm_id || '',
        tiktok_pixel_id: c.data.tiktok_pixel_id || '',
      });
      setEmailStatus(es.data);
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem('tv_admin_token');
        navigate('/dashboard/login');
      }
    } finally { setLoading(false); }
  };

  const reloadAnalytics = async (range) => {
    const { start, end, cmpStart, cmpEnd } = range || {};
    const { data } = await api.get('/analytics', { params: { start: start || undefined, end: end || undefined, cmp_start: cmpStart || undefined, cmp_end: cmpEnd || undefined } });
    setAnalytics(data);
  };

  const logout = () => {
    localStorage.removeItem('tv_admin_token');
    navigate('/dashboard/login');
  };

  const reloadLineup = async () => {
    try { const { data } = await api.get('/lineup/all'); setLineup(data); } catch (e) { /* ignore */ }
  };

  const reloadMediaPartners = async () => {
    try { const { data } = await api.get('/media-partners/all'); setMediaPartners(data); } catch (e) { /* ignore */ }
  };

  const savePixels = async () => {
    await api.put('/config', pixels);
    setSavedMsg('Pixel configuration saved!');
    setTimeout(() => setSavedMsg(''), 2500);
  };

  const NAV = [
    { key: 'analytics', label: 'Analytics', icon: BarChart3 },
    { key: 'registrations', label: 'Creators', icon: Users, count: regs.length },
    { key: 'floorplan', label: 'Floorplan Mapping', icon: MapIcon },
    { key: 'corporate', label: 'Brand Booth', icon: Building2, count: corporate.length },
    { key: 'fnb', label: 'F&B', icon: UtensilsCrossed, count: fnb.length },
    { key: 'lineup', label: 'Line Up', icon: LayoutGrid, count: lineup.length },
    { key: 'media', label: 'Media', icon: Newspaper, count: mediaPartners.length },
    { key: 'shortlinks', label: 'Short URL', icon: Link2 },
    { key: 'marquee', label: 'Marquee', icon: Megaphone },
    { key: 'socials', label: 'Social Media', icon: Share2Icon },
    { key: 'proposal', label: 'Proposal', icon: FileText },
    { key: 'pixels', label: 'Pixels', icon: Target },
    { key: 'ipwhitelist', label: 'IP Whitelist', icon: ShieldCheck },
    { key: 'email', label: 'Email', icon: Mail },
    { key: 'automation', label: 'Otomasi', icon: BellRingIcon },
  ];
  const currentNav = NAV.find((n) => n.key === tab) || NAV[0];
  const rootStyle = { '--tv-accent': theme.accent, '--tv-accent2': theme.accent2, '--tv-bg': theme.bg, '--tv-surface': theme.surface, background: theme.bg };
  const widthClass = { compact: 'max-w-4xl', normal: 'max-w-6xl', full: 'max-w-none' }[containerSize] || 'max-w-6xl';

  return (
    <div className={`tv-dash relative isolate flex h-screen w-full overflow-hidden${isLightColor(theme.bg) ? ' tv-light' : ''}`} style={rootStyle} onClick={() => { if (appearanceOpen) setAppearanceOpen(false); if (bellOpen) setBellOpen(false); }}>
      {dashBg.image_url && (<>
        <div aria-hidden="true" data-testid="dash-bg-layer" className="pointer-events-none absolute inset-0" style={{ zIndex: -1, backgroundImage: `url(${resolveImageUrl(dashBg.image_url)})`, backgroundSize: dashBg.fit === 'repeat' ? 'auto' : dashBg.fit, backgroundRepeat: dashBg.fit === 'repeat' ? 'repeat' : 'no-repeat', backgroundPosition: 'center', filter: dashBg.blur ? `blur(${dashBg.blur}px)` : undefined, transform: dashBg.blur ? 'scale(1.06)' : undefined }} />
        <div aria-hidden="true" className="pointer-events-none absolute inset-0" style={{ zIndex: -1, background: theme.bg, opacity: (Number(dashBg.overlay) || 0) / 100 }} />
      </>)}
      {/* Sidebar icon rail */}
      <aside className="w-20 flex-shrink-0 flex flex-col items-center py-5 bg-[#160F0F] border-r border-[#E63329]/10 z-30">
        <Link to="/" title="Public site" data-testid="nav-public"
          className="w-11 h-11 rounded-xl grid place-items-center bg-[#E63329] text-white mb-6 hover:scale-105 transition-transform shadow-[0_0_20px_rgba(230,51,41,0.4)] font-display text-lg">T</Link>
        <nav className="flex-1 flex flex-col gap-1.5">
          {NAV.map((n) => {
            const Icon = n.icon;
            const active = tab === n.key;
            const navNew = Object.entries(newByType).some(([type, c]) => REG_NAVKEY[type] === n.key && c > 0);
            return (
              <button key={n.key} data-testid={`nav-${n.key}`} title={n.label} onClick={() => setTab(n.key)}
                className={`relative w-12 h-12 rounded-xl grid place-items-center group transition-colors ${active ? 'bg-[#E63329] text-white shadow-[0_0_18px_rgba(230,51,41,0.45)]' : 'text-white/55 hover:text-white hover:bg-white/5'}`}>
                <Icon className="w-5 h-5" />
                {navNew && <span data-testid={`nav-new-${n.key}`} className="absolute top-1 right-1 w-2.5 h-2.5 rounded-full bg-[#F5C518] ring-2 ring-[#160F0F] animate-pulse" />}
                {typeof n.count === 'number' && n.count > 0 && (
                  <span className="absolute -top-0.5 -right-0.5 text-[9px] font-black bg-[#F5C518] text-black rounded-full min-w-[16px] h-4 px-1 grid place-items-center">{n.count}</span>
                )}
                <span className="absolute left-14 whitespace-nowrap px-2 py-1 rounded-md bg-[#160F0F] border border-[#E63329]/20 text-xs font-semibold opacity-0 group-hover:opacity-100 pointer-events-none z-50 shadow-lg">{n.label}</span>
              </button>
            );
          })}
        </nav>
        <button onClick={logout} data-testid="logout-btn" title="Logout"
          className="w-12 h-12 rounded-xl grid place-items-center text-white/55 hover:text-white hover:bg-[#E63329]/20 transition-colors mt-2"><LogOut className="w-5 h-5" /></button>
      </aside>
      <MobileNav nav={NAV} tab={tab} setTab={setTab} regs={regs} corporate={corporate} fnb={fnb} onAppearance={() => setAppearanceOpen(true)} onRefresh={load} onLogout={logout} />

      {/* Main */}
      <div className="flex-1 flex flex-col h-screen overflow-hidden">
        <header className="flex-shrink-0 flex items-center justify-between px-6 md:px-8 py-4 bg-[#0F0A0A]/80 backdrop-blur-xl border-b border-[#E63329]/10 z-20">
          <div>
            <div className="font-display text-xl md:text-2xl text-white leading-none">{currentNav.label}</div>
            <div className="text-[11px] uppercase tracking-widest text-white/40 mt-1">TalesVerse · Memories Market</div>
          </div>
          <div className="flex items-center gap-2">
            <div data-testid="online-counter" title={onlineByPath.map((p) => `${p.path}: ${p.count}`).join('\n') || 'Tidak ada pengunjung aktif'}
              className="inline-flex items-center gap-2 bg-emerald-500/10 border border-emerald-500/30 rounded-xl px-3 py-2 text-sm">
              <span className="relative flex h-2.5 w-2.5">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
              </span>
              <span className="font-black text-emerald-300 tabular-nums" data-testid="online-count">{online}</span>
              <span className="text-emerald-200/70 text-xs hidden sm:inline">online</span>
            </div>
            <div className="hidden lg:flex items-center gap-2 bg-white/5 border border-white/10 rounded-xl px-3 py-2 text-white/30 text-sm"><Search className="w-4 h-4" /><span>Search…</span></div>
            <div className="relative">
              <button onClick={(e) => { e.stopPropagation(); setBellOpen((v) => !v); }} data-testid="notif-bell" title="Notifikasi pendaftar"
                className="relative bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-xl p-2.5 transition-colors">
                <Bell className="w-4 h-4" />
                {newRegs.length > 0 && <span data-testid="notif-badge" className="absolute -top-1.5 -right-1.5 min-w-[18px] h-[18px] px-1 text-[10px] font-black grid place-items-center rounded-full bg-[#E63329] text-white border border-[#160F0F] animate-pulse">{newRegs.length}</span>}
              </button>
              {bellOpen && (
                <div className="absolute right-0 top-12 w-80 tv-card p-0 z-50 shadow-2xl overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="notif-panel">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-white/10">
                    <div className="font-display text-base inline-flex items-center gap-2"><Bell className="w-4 h-4 text-[#F5C518]" /> Pendaftar</div>
                    <div className="flex items-center gap-2">
                      <button onClick={() => setSoundOn((v) => !v)} data-testid="notif-sound-toggle" title={soundOn ? 'Matikan suara' : 'Nyalakan suara'}
                        className={`text-[10px] font-bold px-2 py-1 rounded-md border ${soundOn ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30' : 'bg-white/5 text-white/50 border-white/10'}`}>{soundOn ? '🔊 ON' : '🔇 OFF'}</button>
                      <button onClick={() => setBellOpen(false)} className="p-1 hover:bg-white/10 rounded"><X className="w-4 h-4" /></button>
                    </div>
                  </div>
                  {newRegs.length > 0 && (
                    <div className="px-4 py-2 flex items-center justify-between bg-[#E63329]/10 border-b border-white/10">
                      <span className="text-xs font-bold text-[#F5C518]">{newRegs.length} pendaftar baru</span>
                      <button onClick={markRegsRead} data-testid="notif-mark-read" className="text-[10px] font-bold uppercase tracking-wider px-2 py-1 rounded-md bg-[#E63329] text-white hover:bg-[#F0483E]">Tandai dibaca</button>
                    </div>
                  )}
                  <div className="max-h-80 overflow-y-auto divide-y divide-white/5">
                    {recentRegs.length === 0 && <div className="px-4 py-6 text-center text-sm text-white/40">Belum ada pendaftar.</div>}
                    {recentRegs.slice(0, 15).map((r) => {
                      const isNew = regLastSeen && r.submitted_at > regLastSeen;
                      return (
                        <button key={`${r.type}-${r.id}`} onClick={() => { setTab(REG_NAVKEY[r.type]); setBellOpen(false); }}
                          className={`w-full text-left px-4 py-2.5 hover:bg-white/5 transition-colors flex items-center gap-3 ${isNew ? 'bg-[#F5C518]/5' : ''}`}>
                          {isNew && <span className="w-2 h-2 rounded-full bg-[#E63329] flex-shrink-0" />}
                          <div className="flex-1 min-w-0">
                            <div className="text-sm font-semibold text-white/90 truncate">{r.name}</div>
                            <div className="text-[11px] text-white/45">{parseServerDate(r.submitted_at).toLocaleString()}</div>
                          </div>
                          <span className={`text-[9px] font-black uppercase px-1.5 py-0.5 rounded flex-shrink-0 ${r.type === 'corporate' ? 'bg-white/10 text-white/80' : r.type === 'fnb' ? 'bg-orange-500/20 text-orange-300' : 'bg-[#F5C518]/20 text-[#F5C518]'}`}>{REG_LABEL[r.type]}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            </div>
            <div className="relative">
              <button onClick={(e) => { e.stopPropagation(); setAppearanceOpen((v) => !v); }} data-testid="appearance-btn" title="Tampilan"
                className="bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-xl px-3 py-2 text-sm inline-flex items-center gap-1.5 transition-colors"><Palette className="w-4 h-4" /><span className="hidden sm:inline">Tampilan</span></button>
              {appearanceOpen && <AppearancePanel theme={theme} setTheme={setTheme} containerSize={containerSize} setContainerSize={setContainerSize} onClose={() => setAppearanceOpen(false)} dashBg={dashBg} setDashBg={setDashBg} onBgUpload={uploadDashBg} bgUploading={bgUploading} />}
            </div>
            <button onClick={load} data-testid="refresh-btn" className="bg-white/5 hover:bg-white/10 border border-white/10 text-white rounded-xl px-3 py-2 text-sm inline-flex items-center gap-1.5 transition-colors"><RefreshCw className="w-4 h-4" /><span className="hidden sm:inline">Refresh</span></button>
            <div className="w-9 h-9 rounded-full grid place-items-center text-white font-black text-sm" style={{ background: `linear-gradient(135deg, ${theme.accent}, ${theme.accent2})` }}>TV</div>
          </div>
        </header>

        <main className="flex-1 overflow-y-auto p-5 md:p-8">
          <div className={`${widthClass} mx-auto w-full`}>
          {loading && <div className="text-center py-20 text-white/50 inline-flex items-center gap-2 w-full justify-center"><Loader2 className="w-5 h-5 animate-spin" /> Loading…</div>}

          {tab === 'analytics' && <MobileHero online={online} regs={regs} corporate={corporate} fnb={fnb} />}
          <MobileSubTabs tab={tab} setTab={setTab} regs={regs} corporate={corporate} fnb={fnb} lineup={lineup} mediaPartners={mediaPartners} />
          <div key={tab} className="tv-fade">
            {!loading && tab === 'analytics' && <HomeCommand regs={regs} corporate={corporate} fnb={fnb} lineup={lineup} setTab={setTab} />}
            {!loading && tab === 'analytics' && analytics && (
              <AnalyticsTab analytics={analytics} onRange={reloadAnalytics} accent={theme.accent} accent2={theme.accent2} online={online} onlineByPath={onlineByPath} />
            )}
            {!loading && tab === 'automation' && <AutomationTab />}
          {!loading && tab === 'registrations' && (
              <RegistrationsTab regs={regs} onReload={load} />
            )}
            {!loading && tab === 'floorplan' && (
            <FloorplanTab regs={regs} corporate={corporate} fnb={fnb} lineup={lineup} onReload={load} />
          )}
          {!loading && tab === 'corporate' && (
              <CorporateTab regs={corporate} onReload={load} />
            )}
            {!loading && tab === 'fnb' && (
              <FnbTab regs={fnb} onReload={load} />
            )}
            {!loading && tab === 'lineup' && (
              <LineupTab entries={lineup} onReload={reloadLineup} />
            )}
            {!loading && tab === 'media' && (
              <MediaPartnersTab entries={mediaPartners} onReload={reloadMediaPartners} />
            )}
            {!loading && tab === 'marquee' && (
              <MarqueeTab items={marqueeItems} onSaved={setMarqueeItems} />
            )}
            {!loading && tab === 'socials' && (
              <SocialsTab />
            )}
            {!loading && tab === 'proposal' && (
              <ProposalTab proposal={proposal} onSaved={setProposal} />
            )}
            {!loading && tab === 'pixels' && (
              <PixelsTab pixels={pixels} setPixels={setPixels} save={savePixels} savedMsg={savedMsg} />
            )}
            {!loading && tab === 'ipwhitelist' && (
              <IPWhitelistTab data={ipWhitelist} onChange={setIpWhitelist} />
            )}
            {!loading && tab === 'email' && (
              <EmailTab status={emailStatus} onRefresh={load} />
            )}
{!loading && tab === 'shortlinks' && (
<ShortLinksTab />
)}
          </div>
          </div>
        </main>
      </div>

      {toastReg && (
        <div data-testid="notif-toast" onClick={() => { setTab(REG_NAVKEY[toastReg.type]); setToastReg(null); }}
          className="fixed bottom-6 right-6 z-[60] tv-card p-4 pr-5 flex items-center gap-3 cursor-pointer max-w-sm shadow-2xl border-l-4" style={{ borderLeftColor: theme.accent, animation: 'tvFade 0.3s ease both' }}>
          <div className="w-10 h-10 rounded-xl grid place-items-center flex-shrink-0" style={{ background: `${theme.accent}22` }}><Bell className="w-5 h-5" style={{ color: theme.accent }} /></div>
          <div className="min-w-0">
            <div className="text-[11px] font-black uppercase tracking-widest text-white/50">Pendaftar {REG_LABEL[toastReg.type]} baru</div>
            <div className="text-sm font-bold text-white truncate">{toastReg.name}</div>
          </div>
        </div>
      )}
    </div>
  );
}

function StatCard({ icon: Icon, label, value, tint = 'bg-white' }) {
  return (
    <div className={`${tint} border-2 border-[#1a1a1a] rounded-2xl p-4 shadow-[4px_4px_0_#1a1a1a]`}>
      <div className="flex items-center gap-2 text-black/70">
        <Icon className="w-4 h-4"/>
        <span className="text-[11px] uppercase tracking-widest font-bold">{label}</span>
      </div>
      <div className="outline-title text-4xl mt-1 text-[#1a1a1a]" style={{WebkitTextStroke: '2px #1a1a1a', textShadow:'3px 3px 0 #1a1a1a'}}>{value}</div>
    </div>
  );
}

// Server menyimpan waktu dalam UTC tanpa penanda zona -> tambahkan 'Z' supaya browser menampilkan jam lokal (WIB)
function parseServerDate(v) {
  if (!v) return new Date(NaN);
  if (v instanceof Date) return v;
  const str = String(v);
  return new Date(/([zZ]|[+-]\d\d:?\d\d)$/.test(str) || !/T\d/.test(str) ? str : str + 'Z');
}

function fmtDate(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}
function pctDelta(cur, prev) {
  if (!prev) return cur > 0 ? 100 : 0;
  return ((cur - prev) / prev) * 100;
}

function DeltaStat({ icon: Icon, label, value, prev, suffix = '', tint }) {
  const d = pctDelta(value, prev ?? 0);
  const up = d >= 0;
  return (
    <div data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`} className="tv-card p-4 transition-transform duration-300 hover:-translate-y-1">
      <div className="flex items-center justify-between">
        <div className={`w-10 h-10 rounded-xl grid place-items-center ${tint}`}><Icon className="w-4.5 h-4.5" /></div>
        <span className={`inline-flex items-center gap-0.5 text-[11px] font-black px-2 py-0.5 rounded-full ${up ? 'bg-emerald-500/15 text-emerald-400' : 'bg-red-500/15 text-red-400'}`}>
          {up ? <ArrowUp className="w-3 h-3" /> : <ArrowDown className="w-3 h-3" />}{Math.abs(d).toFixed(0)}%
        </span>
      </div>
      <div className="text-4xl font-black mt-3 leading-none tabular-nums tracking-tight text-white">{value}{suffix}</div>
      <div className="text-[11px] uppercase tracking-widest font-bold text-white/50 mt-1.5">{label}</div>
      <div className="text-[10px] text-white/35 mt-0.5">periode lalu: {prev ?? 0}{suffix}</div>
    </div>
  );
}

function CategoryFunnel({ f }) {
  const stages = [
    { label: 'Klik / Visit', value: f.visit },
    { label: 'Isi Form', value: f.form_start },
    { label: 'Submit', value: f.submit },
    { label: 'Approved / Bayar', value: f.approved },
  ];
  const base = Math.max(1, f.visit, f.form_start, f.submit, f.approved);
  const colors = ['#E63329', '#F5A623', '#F5C518', '#2E9E5B'];
  return (
    <div className="bg-[#0F0A0A] border border-white/5 rounded-2xl p-4" data-testid={`funnel-${f.key}`}>
      <h4 className="font-display text-sm mb-3 text-white">{f.label}</h4>
      <div className="space-y-2.5">
        {stages.map((s, i) => {
          const prevVal = i === 0 ? null : stages[i - 1].value;
          const rate = prevVal !== null ? (prevVal ? Math.round((s.value / prevVal) * 100) : 0) : null;
          return (
            <div key={s.label}>
              <div className="flex items-center justify-between text-xs font-semibold mb-0.5 text-white/80">
                <span>{s.label}</span>
                <span className="text-white/55">{s.value}{rate !== null && <span className="ml-1 text-[10px] text-white/35">({rate}%)</span>}</span>
              </div>
              <div className="h-5 bg-white/5 rounded-md overflow-hidden">
                <div className="h-full rounded-md transition-[width] duration-500" style={{ width: `${Math.max(3, (s.value / base) * 100)}%`, backgroundColor: colors[i] }} />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

const CHART_PALETTE = ['#E63329', '#F5C518', '#3B82F6', '#A855F7', '#10B981', '#EC4899', '#F97316', '#14B8A6'];
const chartTooltipStyle = { backgroundColor: '#160F0F', border: '1px solid rgba(230,51,41,0.25)', borderRadius: '12px', color: '#fff', fontSize: '12px' };

function AnalyticsTab({ analytics, onRange, accent = '#E63329', accent2 = '#F5C518', online = 0, onlineByPath = [] }) {
  const saved = (() => { try { return JSON.parse(localStorage.getItem('tv_analytics_range') || '{}'); } catch (e) { return {}; } })();
  const [preset, setPreset] = useState(saved.preset || '14d');
  const [custom, setCustom] = useState({ start: saved.start || analytics.range?.start || '', end: saved.end || analytics.range?.end || '' });
  const [cmpOn, setCmpOn] = useState(!!saved.cmpOn);
  const [cmp, setCmp] = useState({ start: saved.cmpStart || '', end: saved.cmpEnd || '' });
  const [busy, setBusy] = useState(false);
  const [onlineHistory, setOnlineHistory] = useState([]);
  const [onlinePeak, setOnlinePeak] = useState(0);
  const [evPage, setEvPage] = useState(0);

  useEffect(() => {
    let alive = true;
    const loadHist = async () => { try { const { data } = await api.get('/presence/history', { params: { minutes: 60 } }); if (alive) { setOnlineHistory(data.series || []); setOnlinePeak(data.peak || 0); } } catch (e) {} };
    loadHist();
    const t = setInterval(loadHist, 30000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const palette = [accent, accent2, '#3B82F6', '#A855F7', '#10B981', '#EC4899', '#F97316', '#14B8A6'];
  const cur = analytics.current || { sources: [], funnel: [], conversions: [] };
  const prev = analytics.previous || {};
  const daily = analytics.daily || [];
  const dailyCompare = analytics.daily_compare || [];
  const sources = cur.sources || [];
  const sourceTotal = sources.reduce((a, s) => a + s.count, 0) || 1;
  const sourceMax = Math.max(1, ...sources.map((s) => s.count));
  const conversions = cur.conversions || [];
  const funnel = cur.funnel || [];
  const EV_SIZE = 5; const evAll = analytics.recent || []; const evPages = Math.max(1, Math.ceil(evAll.length / EV_SIZE)); const evP = Math.min(evPage, evPages - 1); const recentPage = evAll.slice(evP * EV_SIZE, evP * EV_SIZE + EV_SIZE);

  // Visits chart: overlay comparison aligned by day index when comparison is on
  const visitsData = daily.map((d, i) => {
    const row = { date: d.date.slice(5), Visits: d.count };
    if (cmpOn && dailyCompare[i]) { row.Pembanding = dailyCompare[i].count; row.cmpDate = dailyCompare[i].date.slice(5); }
    return row;
  });
  const trendSources = analytics.source_trend?.sources || [];
  const cmpTrend = analytics.source_trend_compare;
  const trendData = (analytics.source_trend?.days || []).map((d, i) => {
    const row = { date: d.date.slice(5) };
    trendSources.forEach((s) => { row[s] = d.counts[s] || 0; });
    if (cmpOn && cmpTrend?.days?.[i]) { trendSources.forEach((s) => { row[`${s} (cmp)`] = cmpTrend.days[i].counts[s] || 0; }); }
    return row;
  });

  const persist = (obj) => localStorage.setItem('tv_analytics_range', JSON.stringify(obj));

  const applyPreset = async (p) => {
    const end = new Date();
    const start = new Date();
    const n = { today: 1, '7d': 7, '14d': 14, '30d': 30, '90d': 90 }[p];
    start.setDate(end.getDate() - (n - 1));
    const s = fmtDate(start), e = fmtDate(end);
    setPreset(p); setCustom({ start: s, end: e });
    persist({ preset: p, start: s, end: e, cmpOn, cmpStart: cmp.start, cmpEnd: cmp.end });
    setBusy(true);
    try { await onRange({ start: s, end: e, cmpStart: cmpOn ? cmp.start : '', cmpEnd: cmpOn ? cmp.end : '' }); } finally { setBusy(false); }
  };
  const applyCustom = async () => {
    if (!custom.start || !custom.end) return;
    setPreset('custom');
    persist({ preset: 'custom', start: custom.start, end: custom.end, cmpOn, cmpStart: cmp.start, cmpEnd: cmp.end });
    setBusy(true);
    try { await onRange({ start: custom.start, end: custom.end, cmpStart: cmpOn ? cmp.start : '', cmpEnd: cmpOn ? cmp.end : '' }); } finally { setBusy(false); }
  };

  const presetBtn = (key, lbl) => (
    <button key={key} data-testid={`range-${key}`} onClick={() => applyPreset(key)}
      className={`text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-lg transition-colors ${preset === key ? 'bg-[#E63329] text-white shadow-[0_0_14px_rgba(230,51,41,0.4)]' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}>{lbl}</button>
  );
  const dateInput = 'text-sm bg-[#0A0707] border border-white/10 rounded-lg px-2.5 py-1.5 text-white focus:outline-none focus:border-[#E63329]';

  return (
    <div className="space-y-6">
      {/* Date range toolbar */}
      <MobileCollapse><div className="tv-card p-5">
        <div className="flex flex-wrap items-end gap-5">
          <div>
            <div className="text-[11px] font-black uppercase tracking-widest mb-2 inline-flex items-center gap-1.5 text-white/60"><CalendarDays className="w-3.5 h-3.5 text-[#F5C518]" /> Rentang Cepat</div>
            <div className="flex flex-wrap gap-1.5">
              {presetBtn('today', 'Hari Ini')}{presetBtn('7d', '7 Hari')}{presetBtn('14d', '14 Hari')}{presetBtn('30d', '30 Hari')}{presetBtn('90d', '90 Hari')}
            </div>
          </div>
          <div className="flex items-end gap-2">
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider mb-1.5 text-white/50">Mulai</div>
              <input data-testid="range-start" type="date" value={custom.start} onChange={(e) => setCustom((s) => ({ ...s, start: e.target.value }))} className={dateInput} />
            </div>
            <div>
              <div className="text-[10px] font-bold uppercase tracking-wider mb-1.5 text-white/50">Akhir</div>
              <input data-testid="range-end" type="date" value={custom.end} onChange={(e) => setCustom((s) => ({ ...s, end: e.target.value }))} className={dateInput} />
            </div>
            <button data-testid="range-apply" onClick={applyCustom} disabled={busy}
              className="text-[11px] font-bold uppercase tracking-wider px-4 py-2 rounded-lg bg-[#E63329] text-white hover:bg-[#F0483E] hover:shadow-[0_0_18px_rgba(230,51,41,0.4)] disabled:opacity-50 inline-flex items-center gap-1.5 transition-all">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Filter className="w-3.5 h-3.5" />} Terapkan
            </button>
          </div>
        </div>

        <div className="mt-4 pt-4 border-t border-white/5">
          <label className="inline-flex items-center gap-2 text-[11px] font-bold uppercase tracking-wider text-white/60 cursor-pointer">
            <input data-testid="cmp-toggle" type="checkbox" checked={cmpOn} onChange={(e) => setCmpOn(e.target.checked)} className="accent-[#E63329] w-4 h-4" />
            Bandingkan dengan tanggal custom
          </label>
          {cmpOn && (
            <div className="flex flex-wrap items-end gap-2 mt-2">
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider mb-1.5 text-white/50">Pembanding Mulai</div>
                <input data-testid="cmp-start" type="date" value={cmp.start} onChange={(e) => setCmp((s) => ({ ...s, start: e.target.value }))} className={dateInput} />
              </div>
              <div>
                <div className="text-[10px] font-bold uppercase tracking-wider mb-1.5 text-white/50">Pembanding Akhir</div>
                <input data-testid="cmp-end" type="date" value={cmp.end} onChange={(e) => setCmp((s) => ({ ...s, end: e.target.value }))} className={dateInput} />
              </div>
            </div>
          )}
        </div>

        <div className="text-[11px] font-semibold text-white/50 mt-3">
          Periode: <b className="text-white/80">{analytics.range?.start}</b> → <b className="text-white/80">{analytics.range?.end}</b> · dibandingkan dengan <b className="text-white/80">{analytics.compare_range?.start}</b> → <b className="text-white/80">{analytics.compare_range?.end}</b>
        </div>
      </div></MobileCollapse>

      {/* Online now + trend */}
      <div className="grid lg:grid-cols-3 gap-4" data-testid="online-section">
        <div className="tv-card p-5 flex flex-col justify-between" data-testid="online-card">
          <div className="flex items-center gap-2 text-[11px] font-black uppercase tracking-widest text-white/50">
            <span className="relative flex h-2.5 w-2.5">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2.5 w-2.5 bg-emerald-500" />
            </span>
            Sedang Online
          </div>
          <div className="my-2">
            <div className="text-6xl font-black tabular-nums text-white leading-none" data-testid="online-card-count">{online}</div>
            <div className="text-xs text-white/40 mt-1">pengunjung aktif saat ini · puncak 60 mnt: {onlinePeak}</div>
          </div>
          <div className="space-y-1.5 mt-1">
            {onlineByPath.length === 0 && <div className="text-xs text-white/35">Tidak ada pengunjung aktif.</div>}
            {onlineByPath.slice(0, 4).map((p) => (
              <div key={p.path} className="flex items-center justify-between text-xs">
                <span className="text-white/70 truncate max-w-[70%] font-mono">{p.path}</span>
                <span className="font-bold text-emerald-400">{p.count}</span>
              </div>
            ))}
          </div>
        </div>

        <div className="tv-card p-5 lg:col-span-2" data-testid="online-trend">
          <h3 className="outline-title text-xl mb-3 inline-flex items-center gap-2"><TrendingUp className="w-5 h-5 text-emerald-400" /> Tren Online (60 menit)</h3>
          <div className="h-[180px] w-full" style={{ minHeight: 180 }}>
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={onlineHistory} margin={{ top: 8, right: 12, left: -24, bottom: 0 }}>
                <defs>
                  <linearGradient id="onlineFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                    <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="time" stroke="rgba(255,255,255,0.2)" tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 10 }} tickLine={false} interval={9} />
                <YAxis stroke="rgba(255,255,255,0.2)" tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 10 }} tickLine={false} allowDecimals={false} width={30} />
                <Tooltip contentStyle={chartTooltipStyle} cursor={{ stroke: 'rgba(16,185,129,0.4)' }} />
                <Area type="monotone" dataKey="online" stroke="#10B981" strokeWidth={2.5} fill="url(#onlineFill)" activeDot={{ r: 4 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      {/* Visits + Tren sumber: 1 row */}
      <div className={`grid gap-6 ${trendSources.length > 0 ? 'lg:grid-cols-2' : ''}`}>
      <div className="tv-card p-5" data-testid="chart-visits">
        <div className="flex items-center justify-between mb-4">
          <h3 className="outline-title text-2xl inline-flex items-center gap-2"><TrendingUp className="w-5 h-5" /> Visits per Hari</h3>
          {cmpOn && <span className="text-[11px] font-bold text-white/50 inline-flex items-center gap-1.5"><span className="w-5 border-t-2 border-dashed" style={{ borderColor: accent2 }} /> Pembanding</span>}
        </div>
        <div className="h-[280px] w-full" style={{ minHeight: 280 }}>
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={visitsData} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
              <defs>
                <linearGradient id="visitsFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor={accent} stopOpacity={0.5} />
                  <stop offset="100%" stopColor={accent} stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
              <XAxis dataKey="date" stroke="rgba(255,255,255,0.2)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} tickLine={false} />
              <YAxis stroke="rgba(255,255,255,0.2)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} tickLine={false} allowDecimals={false} />
              <Tooltip contentStyle={chartTooltipStyle} cursor={{ stroke: 'rgba(255,255,255,0.2)' }} />
              {cmpOn && <Area type="monotone" dataKey="Pembanding" stroke={accent2} strokeWidth={2} strokeDasharray="5 4" fill="none" dot={false} />}
              <Area type="monotone" dataKey="Visits" stroke={accent} strokeWidth={3} fill="url(#visitsFill)" activeDot={{ r: 5, fill: accent2 }} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      {trendSources.length > 0 && (
        <div className="tv-card p-5" data-testid="source-trend">
          <div className="flex items-center justify-between mb-4">
            <h3 className="outline-title text-2xl inline-flex items-center gap-2"><Globe className="w-5 h-5" /> Tren Sumber per Hari</h3>
            {cmpOn && <span className="text-[11px] font-bold text-white/50">garis putus-putus = pembanding</span>}
          </div>
          <div className="h-[280px] w-full" style={{ minHeight: 280 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={trendData} margin={{ top: 8, right: 12, left: -18, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" vertical={false} />
                <XAxis dataKey="date" stroke="rgba(255,255,255,0.2)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} tickLine={false} />
                <YAxis stroke="rgba(255,255,255,0.2)" tick={{ fill: 'rgba(255,255,255,0.5)', fontSize: 11 }} tickLine={false} allowDecimals={false} />
                <Tooltip contentStyle={chartTooltipStyle} cursor={{ stroke: 'rgba(255,255,255,0.2)' }} />
                <Legend wrapperStyle={{ fontSize: '12px', color: '#fff' }} />
                {trendSources.map((s, i) => (
                  <Line key={s} type="monotone" dataKey={s} stroke={palette[i % palette.length]} strokeWidth={2.5} dot={false} activeDot={{ r: 4 }} />
                ))}
                {cmpOn && trendSources.map((s, i) => (
                  <Line key={`${s}-cmp`} type="monotone" dataKey={`${s} (cmp)`} stroke={palette[i % palette.length]} strokeWidth={1.8} strokeDasharray="5 4" strokeOpacity={0.55} dot={false} legendType="none" />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        </div>
      )}

      </div>
      {/* KPI cards with period comparison */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <DeltaStat icon={MousePointerClick} label="Total Clicks" value={cur.total_clicks || 0} prev={prev.total_clicks} tint="bg-[#F5C518] text-black" />
        <DeltaStat icon={Users} label="Visits" value={cur.total_visits || 0} prev={prev.total_visits} tint="bg-[#E63329] text-white" />
        <DeltaStat icon={Send} label="Submissions" value={cur.total_submits || 0} prev={prev.total_submits} tint="bg-blue-500 text-white" />
        <DeltaStat icon={CheckCircle2} label="Approved" value={cur.total_approved || 0} prev={prev.total_approved} tint="bg-emerald-500 text-white" />
      </div>

      {/* Conversion rate per link + Traffic sources */}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="tv-card p-5" data-testid="conversion-table">
          <h3 className="outline-title text-2xl mb-1 inline-flex items-center gap-2"><Percent className="w-5 h-5" /> Conversion Rate per Link</h3>
          <p className="text-[11px] text-white/40 mb-3">Visitor → Submit & Visitor → Approved</p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-left text-[10px] uppercase tracking-wider text-white/40 border-b border-white/10">
                <tr><th className="py-2">Kategori</th><th className="text-right">Visits</th><th className="text-right">Submit</th><th className="text-right">% Submit</th><th className="text-right">Approved</th><th className="text-right">% Appr.</th></tr>
              </thead>
              <tbody className="divide-y divide-white/5">
                {conversions.map((c) => (
                  <tr key={c.key} data-testid={`conv-row-${c.key}`}>
                    <td className="py-2.5 font-semibold text-white/90">{c.label}</td>
                    <td className="text-right text-white/70">{c.visits}</td>
                    <td className="text-right text-white/70">{c.submits}</td>
                    <td className="text-right font-bold text-[#F5C518]">{c.submit_rate}%</td>
                    <td className="text-right text-white/70">{c.approved}</td>
                    <td className="text-right font-bold text-emerald-400">{c.approved_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="tv-card p-5" data-testid="traffic-sources">
          <h3 className="outline-title text-2xl mb-3 inline-flex items-center gap-2"><Globe className="w-5 h-5" /> Sumber Traffic</h3>
          {sources.length === 0 && <div className="text-sm text-white/40">Belum ada data sumber. Tambahkan <code className="text-[#F5C518]">?utm_source=instagram</code> dsb. di link kampanye.</div>}
          <div className="space-y-3">
            {sources.map((s, i) => (
              <div key={s.source} data-testid={`source-${s.source}`}>
                <div className="flex items-center justify-between text-sm font-semibold text-white/85">
                  <span className="truncate max-w-[60%]">{s.source}</span>
                  <span className="text-white/60">{s.count} <span className="text-[11px] text-white/40">({Math.round((s.count / sourceTotal) * 100)}%)</span></span>
                </div>
                <div className="h-2 bg-white/5 rounded-full overflow-hidden mt-1.5">
                  <div className="h-full rounded-full transition-[width] duration-500" style={{ width: `${(s.count / sourceMax) * 100}%`, backgroundColor: palette[i % palette.length] }} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Funnel per category */}
      <div className="tv-card p-5">
        <h3 className="outline-title text-2xl mb-4 inline-flex items-center gap-2"><Target className="w-5 h-5 text-[#F5C518]" /> Funnel per Kategori</h3>
        <div className="grid md:grid-cols-3 gap-4">
          {funnel.map((f) => <CategoryFunnel key={f.key} f={f} />)}
        </div>
      </div>

      {/* Recent events */}
      <div className="tv-card p-5">
        <div className="flex items-center justify-between mb-3"><h3 className="outline-title text-2xl">Recent Events</h3>{evAll.length > 0 && <span className="text-[11px] font-bold text-white/45">{evAll.length} event terbaru</span>}</div>
        {analytics.recent.length === 0 && <div className="text-sm text-white/40">No events yet.</div>}
        <div className="divide-y divide-white/5">
          {recentPage.map((r, i) => (
            <div key={i} className="flex items-center justify-between py-2.5 text-sm gap-3">
              <div className="truncate flex-1">
                <div className="font-semibold flex items-center gap-2 text-white/90">
                  {r.label}
                  {r.event === 'form_start' && <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-amber-500/15 text-amber-400">form start</span>}
                </div>
                <div className="text-xs text-white/40 truncate">{r.url}</div>
              </div>
              <span className="text-[10px] font-black uppercase tracking-wider px-2 py-0.5 rounded-full bg-white/5 border border-white/10 text-white/70 whitespace-nowrap">{r.source || 'Direct'}</span>
              <div className="text-xs text-white/50 whitespace-nowrap">{parseServerDate(r.timestamp).toLocaleString()}</div>
            </div>
          ))}
        </div>
        {evPages > 1 && (<div className="flex flex-wrap items-center justify-between gap-2 pt-3 mt-1 border-t border-white/5" data-testid="events-pagination"><span className="text-[11px] text-white/45">Menampilkan {evP * EV_SIZE + 1}-{Math.min(evAll.length, (evP + 1) * EV_SIZE)} dari {evAll.length}</span><div className="flex items-center gap-1"><button type="button" onClick={() => setEvPage(Math.max(0, evP - 1))} disabled={evP === 0} className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-white/5 text-white/80 hover:bg-white/10 disabled:opacity-30">‹ Prev</button>{Array.from({ length: evPages }, (_, n) => n).filter((n) => evPages <= 7 || n === 0 || n === evPages - 1 || Math.abs(n - evP) <= 1).map((n, k, arr) => (<span key={n} className="inline-flex items-center gap-1">{k > 0 && n - arr[k - 1] > 1 && <span className="text-white/30 text-xs px-1">…</span>}<button type="button" onClick={() => setEvPage(n)} className={`text-[11px] font-bold w-7 h-7 rounded-lg ${n === evP ? 'bg-[#E63329] text-white' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}>{n + 1}</button></span>))}<button type="button" onClick={() => setEvPage(Math.min(evPages - 1, evP + 1))} disabled={evP >= evPages - 1} className="text-[11px] font-bold px-2.5 py-1 rounded-lg bg-white/5 text-white/80 hover:bg-white/10 disabled:opacity-30">Next ›</button></div></div>)}
      </div>
    </div>
  );
}

function IPWhitelistTab({ data, onChange }) {
  const [manual, setManual] = useState('');
  const [busy, setBusy] = useState(false);
  const ips = data?.ips || [];
  const currentIp = data?.current_ip || '';
  const alreadyIn = ips.includes(currentIp);

  const addIp = async (ip) => {
    setBusy(true);
    try {
      const { data: res } = await api.post('/ip-whitelist', { ip: ip || '' });
      onChange(res); setManual('');
    } finally { setBusy(false); }
  };
  const removeIp = async (ip) => {
    setBusy(true);
    try { const { data: res } = await api.delete(`/ip-whitelist/${encodeURIComponent(ip)}`); onChange(res); } finally { setBusy(false); }
  };

  return (
    <div className="max-w-3xl space-y-6">
      <div className="tv-card p-6" data-testid="ip-whitelist-panel">
        <h3 className="outline-title text-2xl mb-1 inline-flex items-center gap-2"><ShieldCheck className="w-6 h-6 text-[#F5C518]" /> IP Whitelist</h3>
        <p className="text-sm text-white/50 mb-5">IP pada daftar ini <b className="text-white/80">tidak akan tercatat</b> di analytics internal (kunjungan & klik). Tidak memengaruhi Meta/GA pixel.</p>

        <div className="bg-[#0F0A0A] border border-white/5 rounded-xl p-4 flex flex-wrap items-center justify-between gap-3 mb-5">
          <div>
            <div className="text-[10px] uppercase tracking-widest text-white/40">IP Anda saat ini</div>
            <div className="font-mono text-lg text-white font-bold" data-testid="current-ip">{currentIp || '—'}</div>
          </div>
          <button data-testid="add-my-ip" onClick={() => addIp('')} disabled={busy || alreadyIn}
            className="bg-[#E63329] text-white font-bold rounded-xl px-4 py-2.5 text-sm hover:bg-[#F0483E] hover:shadow-[0_0_18px_rgba(230,51,41,0.4)] disabled:opacity-40 inline-flex items-center gap-2 transition-all">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}{alreadyIn ? 'IP sudah di whitelist' : 'Tambah IP saya sekarang'}
          </button>
        </div>

        <div className="flex gap-2 mb-5">
          <input data-testid="manual-ip-input" value={manual} onChange={(e) => setManual(e.target.value)} placeholder="Tambah IP manual (mis. 103.x.x.x)"
            className="flex-1 bg-[#0A0707] border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-white/30 focus:outline-none focus:border-[#E63329]" />
          <button data-testid="add-manual-ip" onClick={() => manual.trim() && addIp(manual.trim())} disabled={busy || !manual.trim()}
            className="bg-white/5 border border-white/10 text-white font-semibold rounded-xl px-4 py-2.5 text-sm hover:bg-white/10 disabled:opacity-40 inline-flex items-center gap-2"><Plus className="w-4 h-4" /> Tambah</button>
        </div>

        <div className="space-y-2">
          <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold">Daftar Whitelist ({ips.length})</div>
          {ips.length === 0 && <div className="text-sm text-white/40 py-4 text-center bg-[#0F0A0A] rounded-xl border border-white/5">Belum ada IP yang di-whitelist.</div>}
          {ips.map((ip) => (
            <div key={ip} data-testid={`ip-row-${ip}`} className="flex items-center justify-between bg-[#0F0A0A] border border-white/5 rounded-xl px-4 py-3">
              <span className="font-mono text-white/90">{ip}{ip === currentIp && <span className="ml-2 text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-[#F5C518]/20 text-[#F5C518]">Anda</span>}</span>
              <button data-testid={`remove-ip-${ip}`} onClick={() => removeIp(ip)} className="text-white/40 hover:text-red-400 transition-colors"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function RegistrationsTab({ regs, onReload }) {
  const [copied, setCopied] = useState('');
  const [filter, setFilter] = useState('all'); // all | pending | approved | rejected
  const [decision, setDecision] = useState(null); // { reg, status }
  const [note, setNote] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleteState, setDeleteState] = useState(null); // { step: 1|2, reg | null, ids: string[], password, error, bulk: bool }
  const [deleting, setDeleting] = useState(false);
  const [selected, setSelected] = useState(() => new Set());
  const [showTrash, setShowTrash] = useState(false);
  const [trash, setTrash] = useState([]);
  const [trashLoading, setTrashLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  const [editReg, setEditReg] = useState(null);

  const counts = useMemo(() => {
    const c = { all: regs.length, pending: 0, approved: 0, rejected: 0 };
    regs.forEach((r) => { const s = r.status || 'pending'; c[s] = (c[s] || 0) + 1; });
    return c;
  }, [regs]);

  const filtered = useMemo(() => {
    const base = filter === 'all' ? regs : regs.filter((r) => (r.status || 'pending') === filter);
    const q = search.trim().toLowerCase();
    if (!q) return base;
    const qd = q.replace(/\D/g, '');
    return base.filter((r) => [r.brand_name, r.pic_name, r.pic_email, r.pic_phone, r.category, r.other_category, r.partner_brand_name, (r.categories || []).join(' ')]
      .some((v) => String(v || '').toLowerCase().includes(q)) || (qd.length >= 4 && String(r.pic_phone || '').replace(/\D/g, '').includes(qd)));
  }, [regs, filter, search]);
  const REG_PAGE_SIZE = 10;
  const pageCount = Math.max(1, Math.ceil(filtered.length / REG_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const visible = filtered.slice(safePage * REG_PAGE_SIZE, safePage * REG_PAGE_SIZE + REG_PAGE_SIZE);
  useEffect(() => { setPage(0); }, [filter, search]);
  const pageNums = useMemo(() => {
    if (pageCount <= 7) return Array.from({ length: pageCount }, (_, i) => i);
    const s = new Set([0, pageCount - 1, safePage - 1, safePage, safePage + 1].filter((n) => n >= 0 && n < pageCount));
    const arr = Array.from(s).sort((x, y) => x - y);
    const out = [];
    arr.forEach((n, i) => { if (i > 0 && n - arr[i - 1] > 1) out.push('...'); out.push(n); });
    return out;
  }, [pageCount, safePage]);

  const openDecision = (reg, status) => { setDecision({ reg, status }); setNote(''); setSendEmail(true); };
  const closeDecision = () => { if (!submitting) { setDecision(null); setNote(''); } };
  const confirmDecision = async () => {
    if (!decision) return;
    setSubmitting(true);
    try {
      await api.put(`/registrations/${decision.reg.id}/status`, { status: decision.status, note, send_email: sendEmail });
      setDecision(null); setNote('');
      onReload && onReload();
    } catch (e) { alert('Gagal update status: ' + (e?.response?.data?.detail || 'error')); }
    finally { setSubmitting(false); }
  };

  const openDelete = (reg) => setDeleteState({ step: 1, reg, ids: [reg.id], password: '', error: '', bulk: false });
  const openBulkDelete = () => {
    if (selected.size === 0) return;
    setDeleteState({ step: 1, reg: null, ids: Array.from(selected), password: '', error: '', bulk: true });
  };
  const closeDelete = () => { if (!deleting) setDeleteState(null); };

  const confirmDelete = async () => {
    if (!deleteState) return;
    if (deleteState.step === 1) { setDeleteState((s) => ({ ...s, step: 2, error: '' })); return; }
    setDeleting(true);
    try {
      if (deleteState.bulk) {
        await api.post('/registrations/bulk-delete', { password: deleteState.password, ids: deleteState.ids });
      } else {
        await api.delete(`/registrations/${deleteState.ids[0]}`, { data: { password: deleteState.password } });
      }
      setDeleteState(null);
      setSelected(new Set());
      onReload && onReload();
      if (showTrash) loadTrash();
    } catch (e) {
      setDeleteState((s) => ({ ...s, error: e?.response?.data?.detail || 'Gagal menghapus.' }));
    } finally { setDeleting(false); }
  };

  const loadTrash = async () => {
    setTrashLoading(true);
    try {
      const { data } = await api.get('/registrations/trash');
      setTrash(data);
    } catch (e) { console.error(e); }
    finally { setTrashLoading(false); }
  };
  useEffect(() => { if (showTrash) loadTrash(); }, [showTrash]);

  const restoreOne = async (id) => {
    try {
      await api.post(`/registrations/${id}/restore`);
      await loadTrash();
      onReload && onReload();
    } catch (e) { alert('Gagal restore: ' + (e?.response?.data?.detail || 'error')); }
  };

  // Selection helpers
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allSelected = visible.length > 0 && visible.every((r) => selected.has(r.id));
  const someSelected = visible.some((r) => selected.has(r.id));
  const toggleAll = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (allSelected) { visible.forEach((r) => next.delete(r.id)); }
      else { visible.forEach((r) => next.add(r.id)); }
      return next;
    });
  };

  // Export helpers
  const buildRows = () => {
    const headers = ['Submitted At','Status','Status Note','Brand Name','Category','Other Category','PIC Name','PIC Phone','PIC Email','Social Link','Product Showcase','Sharing With Brand','Partner Brand','Sheet Synced'];
    const rows = filtered.map(r => [
      r.submitted_at ? parseServerDate(r.submitted_at).toLocaleString() : '',
      r.status || 'pending', r.status_note || '',
      r.brand_name || '', r.category || (r.categories || []).join(' | '),
      r.other_category || '', r.pic_name || '', r.pic_phone || '', r.pic_email || '',
      r.social_link || '', r.product_showcase_link || '',
      r.sharing_with_brand || '', r.partner_brand_name || '',
      r.synced_to_sheet ? 'Yes' : 'No',
    ]);
    return { headers, rows };
  };
  const timestamp = () => { const d = new Date(); const p = (n) => String(n).padStart(2, '0'); return `${d.getFullYear()}${p(d.getMonth()+1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}`; };
  const exportCSV = () => {
    const { headers, rows } = buildRows();
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = '\uFEFF' + [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `talesverse_registrations_${filter}_${timestamp()}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };
  const exportXLSX = () => {
    const { headers, rows } = buildRows();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = headers.map((h) => ({ wch: Math.max(12, Math.min(40, h.length + 4)) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Registrations');
    XLSX.writeFile(wb, `talesverse_registrations_${filter}_${timestamp()}.xlsx`);
  };
  const copyCsv = () => {
    const { headers, rows } = buildRows();
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
    navigator.clipboard.writeText(csv);
    setCopied('csv'); setTimeout(() => setCopied(''), 1500);
  };

  return (
    <>
      <div className="bg-white border-2 border-[#1a1a1a] rounded-2xl shadow-[4px_4px_0_#1a1a1a]">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b-2 border-[#1a1a1a]">
          <h3 className="outline-title text-2xl">Creator Applications</h3>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={() => setShowTrash((v) => !v)} className={`tv-btn px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5 ${showTrash ? '' : ''}`}>
              <Trash2 className="w-4 h-4"/> {showTrash ? 'Hide Trash' : 'Trash (24h)'}
            </button>
            <button onClick={exportXLSX} className="tv-btn px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5" style={{backgroundColor:'#F5C518'}}>
              <FileSpreadsheet className="w-4 h-4"/> Export Excel
            </button>
            <button onClick={exportCSV} className="tv-btn px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5">
              <Download className="w-4 h-4"/> Export CSV
            </button>
            <button onClick={copyCsv} className="tv-btn px-3 py-1.5 text-xs font-semibold inline-flex items-center gap-1">
              {copied === 'csv' ? <><Check className="w-4 h-4"/> Copied</> : <><Copy className="w-4 h-4"/> Copy</>}
            </button>
          </div>
        </div>

        {/* Bulk toolbar */}
        {selected.size > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 bg-red-50 border-b-2 border-red-500">
            <div className="text-[13px] font-bold text-red-900">
              {selected.size} baris terpilih
            </div>
            <div className="flex items-center gap-2">
              <button onClick={() => setSelected(new Set())} className="text-[11px] font-semibold text-red-800 hover:underline">Clear selection</button>
              <button onClick={openBulkDelete} className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md border-2 border-red-800 bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-1">
                <Trash2 className="w-3.5 h-3.5"/> Delete selected
              </button>
            </div>
          </div>
        )}

        {/* Filter tabs */}
        <div className="flex items-center gap-2 px-4 py-3 border-b border-black/10 bg-[#faf7ef] overflow-x-auto">
          <Filter className="w-4 h-4 text-black/50 flex-shrink-0"/>
          {[
            { v: 'all', l: 'All', cls: 'bg-white text-[#1a1a1a] border-[#1a1a1a]' },
            { v: 'pending', l: 'Pending', cls: 'bg-amber-100 text-amber-800 border-amber-500' },
            { v: 'approved', l: 'Approved', cls: 'bg-emerald-100 text-emerald-800 border-emerald-500' },
            { v: 'rejected', l: 'Rejected', cls: 'bg-red-100 text-red-800 border-red-500' },
          ].map((f) => {
            const active = filter === f.v;
            return (
              <button
                key={f.v}
                onClick={() => setFilter(f.v)}
                className={`text-[12px] font-bold px-3 py-1.5 rounded-full border-2 whitespace-nowrap ${active ? f.cls + ' shadow-[2px_2px_0_currentColor]' : 'bg-white text-black/60 border-black/20 hover:border-black/40'}`}
              >
                {f.l} <span className="opacity-70 font-normal">({counts[f.v] || 0})</span>
              </button>
            );
          })}
          <div className="ml-auto relative flex-shrink-0">
            <Search className="w-4 h-4 text-black/40 absolute left-3 top-1/2 -translate-y-1/2 pointer-events-none"/>
            <input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari brand, PIC, email, no. HP, kategori..." className="w-[280px] pl-9 pr-8 py-1.5 text-[12px] font-semibold rounded-full border-2 border-black/20 bg-white focus:border-[#1a1a1a] outline-none placeholder:font-normal" data-testid="reg-search"/>
            {search && <button onClick={() => setSearch('')} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-black/40 hover:text-black" title="Hapus pencarian"><X className="w-3.5 h-3.5"/></button>}
          </div>
        </div>

        <OnbBar src="creator" regs={regs} />

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f6f1e7] text-left text-[11px] uppercase tracking-wider text-black/60">
              <tr>
                <th className="p-3 w-[36px]">
                  <input type="checkbox" checked={allSelected} ref={(el) => { if (el) el.indeterminate = !allSelected && someSelected; }} onChange={toggleAll} className="w-4 h-4 accent-[#E63329] cursor-pointer" />
                </th>
                <th className="p-3">Date</th>
                <th className="p-3">Status</th>
                <th className="p-3">Brand</th>
                <th className="p-3">PIC / Contact</th>
                <th className="p-3">Category</th>
                <th className="p-3">Sharing</th>
                <th className="p-3">Links</th>
                <th className="p-3">Onboarding</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={10} className="p-6 text-center text-black/50">{search ? <>Tidak ada creator yang cocok dengan "<b>{search}</b>"{filter !== 'all' ? ` di filter ${filter}` : ''}. <button onClick={() => setSearch('')} className="underline font-semibold text-black/70">Hapus pencarian</button></> : <>No registrations{filter !== 'all' ? ` in "${filter}"` : ''}.</>}</td></tr>
              )}
              {visible.map((r) => {
                const cats = r.category ? [r.category] : (r.categories || []);
                const status = r.status || 'pending';
                const isSelected = selected.has(r.id);
                return (
                  <tr key={r.id} className={`border-t border-black/10 align-top ${isSelected ? 'bg-red-50/40' : 'hover:bg-[#fdfbf6]'}`}>
                    <td className="p-3">
                      <input type="checkbox" checked={isSelected} onChange={() => toggleOne(r.id)} className="w-4 h-4 accent-[#E63329] cursor-pointer"/>
                    </td>
                    <td className="p-3 text-xs whitespace-nowrap">{parseServerDate(r.submitted_at).toLocaleString()}</td>
                    <td className="p-3"><StatusPill status={status}/></td>
                    <td className="p-3 font-semibold">{r.brand_name}</td>
                    <td className="p-3 text-xs">
                      <div className="font-semibold">{r.pic_name}</div>
                      <div className="text-black/70">{r.pic_email}</div>
                      <div className="text-black/70">{r.pic_phone}</div>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {cats.map((c) => (
                          <span key={c} className="inline-block bg-[#F5C518] border border-[#1a1a1a] text-[10px] font-bold px-1.5 py-0.5 rounded">{c}</span>
                        ))}
                        {r.other_category && <span className="inline-block bg-white border border-[#1a1a1a] text-[10px] font-bold px-1.5 py-0.5 rounded">Other: {r.other_category}</span>}
                      </div>
                    </td>
                    <td className="p-3 text-xs">
                      {r.sharing_with_brand === 'yes' ? (
                        <div><span className="inline-block bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">YES</span><div className="mt-0.5">{r.partner_brand_name || '—'}</div></div>
                      ) : r.sharing_with_brand === 'no' ? (
                        <span className="inline-block bg-black/70 text-white text-[10px] font-bold px-1.5 py-0.5 rounded">NO</span>
                      ) : <span className="text-black/40">—</span>}
                    </td>
                    <td className="p-3 text-xs whitespace-nowrap">
                      {r.social_link && <a href={r.social_link} target="_blank" rel="noreferrer" className="underline block">social</a>}
                      {r.product_showcase_link && <a href={r.product_showcase_link} target="_blank" rel="noreferrer" className="underline block">showcase</a>}
                    </td>
                    <td className="p-3"><OnbCell src="creator" reg={r} status={status} /></td>
                    <td className="p-3">
                      <div className="flex flex-col gap-1.5 min-w-[112px]">
                        {status !== 'approved' && (<button onClick={() => openDecision(r, 'approved')} disabled={status === 'approved'} className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-md border-2 border-emerald-700 bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1">
                          <ThumbsUp className="w-3 h-3"/> Approve
                        </button>)}
                        {status !== 'rejected' && (<button onClick={() => openDecision(r, 'rejected')} disabled={status === 'rejected'} className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-md border-2 border-red-700 bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1">
                          <ThumbsDown className="w-3 h-3"/> Reject
                        </button>)}
                        {status !== 'pending' && (
                          <button onClick={() => openDecision(r, 'pending')} className="text-[10px] text-black/50 hover:text-black underline">reset to pending</button>
                        )}
                        <button onClick={() => setEditReg(r)} className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-md border-2 border-[#1a1a1a] bg-white hover:bg-[#f6f1e7] inline-flex items-center justify-center gap-1" data-testid={`reg-edit-${r.id}`}>
                          <Pencil className="w-3 h-3"/> Edit
                        </button>
                        <button onClick={() => openDelete(r)} className="text-[10px] text-red-700 hover:text-red-900 underline underline-offset-2 inline-flex items-center justify-center gap-1 mt-0.5">
                          <Trash2 className="w-3 h-3"/> delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div className="flex flex-wrap items-center justify-between gap-3 px-4 py-3 border-t-2 border-[#1a1a1a] bg-[#faf7ef] rounded-b-2xl" data-testid="reg-pagination">
          <div className="text-[11px] font-semibold" style={{ color: '#6b6455' }}>
            {filtered.length === 0 ? 'Tidak ada data' : `Menampilkan ${safePage * REG_PAGE_SIZE + 1}-${Math.min(filtered.length, (safePage + 1) * REG_PAGE_SIZE)} dari ${filtered.length} creator`}
            <span className="text-black/35"> | Ekspor mengikuti filter & pencarian ({filtered.length} baris)</span>
          </div>
          {pageCount > 1 && (
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(Math.max(0, safePage - 1))} disabled={safePage === 0} className="h-8 px-2.5 rounded-md border-2 border-[#1a1a1a] bg-white text-xs font-bold inline-flex items-center gap-1 hover:bg-[#f6f1e7] disabled:opacity-35 disabled:cursor-not-allowed" data-testid="reg-prev"><ChevronLeft className="w-4 h-4"/> Prev</button>
              {pageNums.map((n, i) => (n === '...'
                ? <span key={`gap${i}`} className="px-1 text-xs text-black/40">...</span>
                : <button key={n} onClick={() => setPage(n)} className={`min-w-[32px] h-8 px-2 rounded-md border-2 text-xs font-bold ${n === safePage ? 'bg-[#1a1a1a] text-white border-[#1a1a1a] shadow-[2px_2px_0_#E63329]' : 'bg-white text-black/70 border-black/20 hover:border-[#1a1a1a]'}`} data-testid={`reg-page-${n + 1}`}>{n + 1}</button>))}
              <button onClick={() => setPage(Math.min(pageCount - 1, safePage + 1))} disabled={safePage >= pageCount - 1} className="h-8 px-2.5 rounded-md border-2 border-[#1a1a1a] bg-white text-xs font-bold inline-flex items-center gap-1 hover:bg-[#f6f1e7] disabled:opacity-35 disabled:cursor-not-allowed" data-testid="reg-next">Next <ChevronRight className="w-4 h-4"/></button>
            </div>
          )}
        </div>
      </div>

      {/* Trash panel */}
      {showTrash && (
        <div className="mt-5 bg-white border-2 border-[#1a1a1a] rounded-2xl shadow-[4px_4px_0_#1a1a1a]">
          <div className="flex items-center justify-between p-4 border-b-2 border-[#1a1a1a] bg-[#fff5f4]">
            <h4 className="font-display font-bold text-lg inline-flex items-center gap-2">
              <Trash2 className="w-5 h-5 text-red-700"/> Trash — 24 Jam Terakhir
            </h4>
            <button onClick={loadTrash} className="text-xs underline underline-offset-4 text-black/60 inline-flex items-center gap-1"><RefreshCw className="w-3 h-3"/> Refresh</button>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#f6f1e7] text-left text-[11px] uppercase tracking-wider text-black/60">
                <tr>
                  <th className="p-3">Deleted At</th>
                  <th className="p-3">Expires In</th>
                  <th className="p-3">Brand</th>
                  <th className="p-3">PIC / Email</th>
                  <th className="p-3">Status</th>
                  <th className="p-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody>
                {trashLoading && <tr><td colSpan={6} className="p-6 text-center text-black/50">Loading…</td></tr>}
                {!trashLoading && trash.length === 0 && (
                  <tr><td colSpan={6} className="p-6 text-center text-black/50">Trash kosong. Data yang dihapus akan otomatis hilang setelah 24 jam.</td></tr>
                )}
                {trash.map((r) => {
                  const deletedAt = new Date(r.deleted_at);
                  const expiresAt = new Date(deletedAt.getTime() + 24 * 3600 * 1000);
                  const remainingMs = expiresAt.getTime() - Date.now();
                  const remainingH = Math.max(0, Math.floor(remainingMs / (3600 * 1000)));
                  const remainingM = Math.max(0, Math.floor((remainingMs % (3600 * 1000)) / (60 * 1000)));
                  return (
                    <tr key={r.id} className="border-t border-black/10 hover:bg-[#fdfbf6]">
                      <td className="p-3 text-xs whitespace-nowrap">{deletedAt.toLocaleString()}</td>
                      <td className="p-3 text-xs whitespace-nowrap inline-flex items-center gap-1 text-amber-800 font-semibold">
                        <Clock className="w-3.5 h-3.5"/> {remainingH}j {remainingM}m
                      </td>
                      <td className="p-3 font-semibold">{r.brand_name}</td>
                      <td className="p-3 text-xs">
                        <div>{r.pic_name}</div>
                        <div className="text-black/70">{r.pic_email}</div>
                      </td>
                      <td className="p-3"><StatusPill status={r.status || 'pending'}/></td>
                      <td className="p-3 text-right">
                        <button onClick={() => restoreOne(r.id)} className="text-[11px] font-bold uppercase tracking-wider px-3 py-1.5 rounded-md border-2 border-emerald-700 bg-emerald-500 text-white hover:bg-emerald-600 inline-flex items-center gap-1">
                          <Undo2 className="w-3.5 h-3.5"/> Restore
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {decision && (
        <DecisionDialog decision={decision} note={note} setNote={setNote} sendEmail={sendEmail} setSendEmail={setSendEmail} submitting={submitting} onCancel={closeDecision} onConfirm={confirmDecision} />
      )}

      {editReg && (
        <RegEditDialog reg={editReg} regs={regs} onClose={() => setEditReg(null)} onSaved={() => { setEditReg(null); onReload && onReload(); loadOnb(); }} />
      )}

      {deleteState && (
        <DeleteDialog state={deleteState} setState={setDeleteState} deleting={deleting} onCancel={closeDelete} onConfirm={confirmDelete} />
      )}
    </>
  );
}


function RegEditDialog({ reg, regs, onClose, onSaved }) {
  const [f, setF] = useState(() => ({
    brand_name: reg.brand_name || '',
    category: reg.category || (reg.categories || []).join(', '),
    other_category: reg.other_category || '',
    pic_name: reg.pic_name || '',
    pic_phone: reg.pic_phone || '',
    pic_email: reg.pic_email || '',
    social_link: reg.social_link || '',
    product_showcase_link: reg.product_showcase_link || '',
    sharing_with_brand: reg.sharing_with_brand || '',
    partner_brand_name: reg.partner_brand_name || '',
  }));
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const cats = useMemo(() => Array.from(new Set((regs || []).map((r) => r.category).filter(Boolean))).sort(), [regs]);
  const set = (k) => (e) => setF((o) => ({ ...o, [k]: e.target.value }));
  const close = () => { if (!saving) onClose(); };
  const save = async () => {
    if (!f.brand_name.trim() || !f.pic_name.trim()) { setErr('Nama brand dan nama PIC wajib diisi.'); return; }
    setSaving(true); setErr('');
    try { await api.put(`/registrations/${reg.id}`, f); onSaved(); }
    catch (e) { setErr(e?.response?.data?.detail || 'Gagal menyimpan perubahan.'); }
    finally { setSaving(false); }
  };
  const inp = 'w-full border-2 border-black/20 focus:border-[#1a1a1a] outline-none rounded-lg px-3 py-2 text-sm bg-white';
  const field = (label, key, extra = {}) => (
    <label className={`block ${extra.full ? 'sm:col-span-2' : ''}`}>
      <span className="block text-[11px] font-bold uppercase tracking-wider text-black/60 mb-1">{label}{extra.req ? <span className="text-[#E63329]"> *</span> : null}</span>
      <input value={f[key]} onChange={set(key)} className={inp} list={extra.list} type={extra.type || 'text'} placeholder={extra.ph || ''} data-testid={`reg-edit-${key}`} />
    </label>
  );
  const sect = 'text-[11px] font-black uppercase tracking-[0.15em] text-black/60 mb-2 pb-1 border-b border-black/10';
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto [&>*]:my-auto bg-black/60 p-4" onClick={close}>
      <div className="bg-white rounded-2xl border-2 border-[#1a1a1a] shadow-[8px_8px_0_#1a1a1a] max-w-2xl w-full max-h-[90vh] flex flex-col overflow-hidden" onClick={(e) => e.stopPropagation()} data-testid="reg-edit-dialog">
        <div className="bg-[#F5C518] border-b-2 border-[#1a1a1a] px-5 py-3 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg uppercase tracking-wide inline-flex items-center gap-2"><Pencil className="w-5 h-5"/> Edit Data Creator</h3>
          <button onClick={close} className="hover:bg-black/10 rounded p-1"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5 overflow-y-auto space-y-5">
          <div className="flex flex-wrap items-center gap-2 text-[12px] bg-[#f6f1e7] border border-black/10 rounded-lg px-3 py-2">
            <StatusPill status={reg.status || 'pending'}/>
            <span className="text-black/60">Daftar: {reg.submitted_at ? parseServerDate(reg.submitted_at).toLocaleString() : '-'}</span>
          </div>
          <div>
            <div className={sect}>Brand</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {field('Nama brand', 'brand_name', { req: true, full: true })}
              {field('Kategori', 'category', { list: 'reg-edit-cats' })}
              {field('Kategori lain (opsional)', 'other_category')}
            </div>
            <datalist id="reg-edit-cats">{cats.map((c) => <option key={c} value={c}/>)}</datalist>
          </div>
          <div>
            <div className={sect}>PIC / Kontak</div>
            <div className="grid sm:grid-cols-2 gap-3">
              {field('Nama PIC', 'pic_name', { req: true, full: true })}
              {field('No. HP / WhatsApp', 'pic_phone', { ph: '08xxxxxxxxxx' })}
              {field('Email', 'pic_email', { type: 'email' })}
            </div>
          </div>
          <div>
            <div className={sect}>Link</div>
            <div className="grid gap-3">
              {field('Link sosial media', 'social_link', { ph: 'https://instagram.com/...' })}
              {field('Link showcase produk', 'product_showcase_link', { ph: 'https://...' })}
            </div>
          </div>
          <div>
            <div className={sect}>Sharing booth</div>
            <div className="grid sm:grid-cols-2 gap-3">
              <label className="block">
                <span className="block text-[11px] font-bold uppercase tracking-wider text-black/60 mb-1">Berbagi booth dengan brand lain?</span>
                <select value={f.sharing_with_brand} onChange={set('sharing_with_brand')} className={inp} data-testid="reg-edit-sharing">
                  <option value="">Belum diisi</option>
                  <option value="no">Tidak</option>
                  <option value="yes">Ya</option>
                </select>
              </label>
              {f.sharing_with_brand === 'yes' && field('Nama brand partner', 'partner_brand_name')}
            </div>
          </div>
          <div className="text-[11px] text-black/50">Perubahan otomatis ikut memperbarui data Line Up (jika approved) dan data onboarding tenant ini.</div>
          {err && <div className="text-sm font-semibold text-red-700 bg-red-50 border-2 border-red-300 rounded-lg px-3 py-2" data-testid="reg-edit-error">{err}</div>}
        </div>
        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t-2 border-[#1a1a1a] bg-[#faf7ef]">
          <button onClick={close} disabled={saving} className="px-4 py-2 rounded-lg border-2 border-[#1a1a1a] bg-white text-sm font-bold hover:bg-[#f6f1e7] disabled:opacity-50">Batal</button>
          <button onClick={save} disabled={saving} className="px-4 py-2 rounded-lg border-2 border-[#1a1a1a] bg-[#1a1a1a] text-white text-sm font-bold shadow-[3px_3px_0_#E63329] inline-flex items-center gap-1.5 disabled:opacity-60" data-testid="reg-edit-save">
            {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} Simpan perubahan
          </button>
        </div>
      </div>
    </div>
  );
}


function CorporateTab({ regs, onReload }) {
  const [filter, setFilter] = useState('all');
  const [decision, setDecision] = useState(null); // { reg, status }
  const [note, setNote] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleteState, setDeleteState] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const BOOTH_LABELS = {
    silver: 'Silver (3×2m)',
    gold: 'Gold (4×3m)',
    platinum: 'Platinum (6×3m)',
    custom: 'Custom',
  };

  const counts = useMemo(() => {
    const c = { all: regs.length, pending: 0, approved: 0, rejected: 0 };
    regs.forEach((r) => { const s = r.status || 'pending'; c[s] = (c[s] || 0) + 1; });
    return c;
  }, [regs]);

  const filtered = useMemo(() => {
    if (filter === 'all') return regs;
    return regs.filter((r) => (r.status || 'pending') === filter);
  }, [regs, filter]);

  const openDecision = (reg, status) => { setDecision({ reg, status }); setNote(''); setSendEmail(true); };
  const closeDecision = () => { if (!submitting) { setDecision(null); setNote(''); } };
  const confirmDecision = async () => {
    if (!decision) return;
    setSubmitting(true);
    try {
      await api.put(`/corporate/registrations/${decision.reg.id}/status`, {
        status: decision.status,
        note,
        send_email: sendEmail,
      });
      setDecision(null); setNote('');
      onReload && onReload();
    } catch (e) { alert('Gagal update status: ' + (e?.response?.data?.detail || 'error')); }
    finally { setSubmitting(false); }
  };

  const openDelete = (reg) => setDeleteState({ step: 1, reg, ids: [reg.id], password: '', error: '', bulk: false });
  const closeDelete = () => { if (!deleting) setDeleteState(null); };
  const confirmDelete = async () => {
    if (!deleteState) return;
    if (deleteState.step === 1) { setDeleteState((s) => ({ ...s, step: 2, error: '' })); return; }
    setDeleting(true);
    try {
      await api.delete(`/corporate/registrations/${deleteState.ids[0]}`, { data: { password: deleteState.password } });
      setDeleteState(null);
      onReload && onReload();
    } catch (e) {
      setDeleteState((s) => ({ ...s, error: e?.response?.data?.detail || 'Gagal menghapus.' }));
    } finally { setDeleting(false); }
  };

  const buildRows = () => {
    const headers = ['Submitted At','Status','Company','Industry','Website','PIC Name','PIC Position','PIC Email','PIC Phone','Booth','Sponsorship Interest','Objectives'];
    const rows = filtered.map(r => [
      r.submitted_at ? parseServerDate(r.submitted_at).toLocaleString() : '',
      r.status || 'pending',
      r.company_name || '',
      r.other_industry ? `Other: ${r.other_industry}` : (r.industry || ''),
      r.company_website || '',
      r.pic_name || '',
      r.pic_position || '',
      r.pic_email || '',
      r.pic_phone || '',
      BOOTH_LABELS[r.booth_package] || r.booth_package || '',
      (r.sponsorship_interests || []).join(' | '),
      r.objectives || '',
    ]);
    return { headers, rows };
  };
  const timestamp = () => { const d = new Date(); const p = (n) => String(n).padStart(2, '0'); return `${d.getFullYear()}${p(d.getMonth()+1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}`; };
  const exportXLSX = () => {
    const { headers, rows } = buildRows();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = headers.map((h) => ({ wch: Math.max(12, Math.min(40, h.length + 4)) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Corporate');
    XLSX.writeFile(wb, `talesverse_corporate_${filter}_${timestamp()}.xlsx`);
  };
  const exportCSV = () => {
    const { headers, rows } = buildRows();
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = '\uFEFF' + [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `talesverse_corporate_${filter}_${timestamp()}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <>
      <div className="bg-white border-2 border-[#1a1a1a] rounded-2xl shadow-[4px_4px_0_#1a1a1a]">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b-2 border-[#1a1a1a]">
          <h3 className="outline-title text-2xl">Corporate Applications</h3>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={exportXLSX} className="tv-btn px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5" style={{backgroundColor:'#F5C518'}}>
              <FileSpreadsheet className="w-4 h-4"/> Export Excel
            </button>
            <button onClick={exportCSV} className="tv-btn px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5">
              <Download className="w-4 h-4"/> Export CSV
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 px-4 py-3 border-b border-black/10 bg-[#faf7ef] overflow-x-auto">
          <Filter className="w-4 h-4 text-black/50 flex-shrink-0"/>
          {[
            { v: 'all', l: 'All', cls: 'bg-white text-[#1a1a1a] border-[#1a1a1a]' },
            { v: 'pending', l: 'Pending', cls: 'bg-amber-100 text-amber-800 border-amber-500' },
            { v: 'approved', l: 'Approved', cls: 'bg-emerald-100 text-emerald-800 border-emerald-500' },
            { v: 'rejected', l: 'Rejected', cls: 'bg-red-100 text-red-800 border-red-500' },
          ].map((f) => {
            const active = filter === f.v;
            return (
              <button key={f.v} onClick={() => setFilter(f.v)}
                className={`text-[12px] font-bold px-3 py-1.5 rounded-full border-2 whitespace-nowrap ${active ? f.cls + ' shadow-[2px_2px_0_currentColor]' : 'bg-white text-black/60 border-black/20 hover:border-black/40'}`}>
                {f.l} <span className="opacity-70 font-normal">({counts[f.v] || 0})</span>
              </button>
            );
          })}
          <div className="ml-auto text-[11px] font-semibold" style={{ color: '#6b6455' }}>Ekspor mengikuti filter aktif ({filtered.length} baris)</div>
        </div>

        <OnbBar src="brand" regs={regs} />

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f6f1e7] text-left text-[11px] uppercase tracking-wider text-black/60">
              <tr>
                <th className="p-3">Date</th>
                <th className="p-3">Status</th>
                <th className="p-3">Company / Industry</th>
                <th className="p-3">PIC / Contact</th>
                <th className="p-3">Booth</th>
                <th className="p-3">Interest</th>
                <th className="p-3">Onboarding</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={8} className="p-6 text-center text-black/50">No corporate applications{filter !== 'all' ? ` in "${filter}"` : ''}.</td></tr>
              )}
              {filtered.map((r) => {
                const status = r.status || 'pending';
                const industry = r.other_industry ? `Other: ${r.other_industry}` : (r.industry || '-');
                return (
                  <tr key={r.id} className="border-t border-black/10 hover:bg-[#fdfbf6] align-top">
                    <td className="p-3 text-xs whitespace-nowrap">{parseServerDate(r.submitted_at).toLocaleString()}</td>
                    <td className="p-3"><StatusPill status={status}/></td>
                    <td className="p-3">
                      <div className="font-semibold">{r.company_name}</div>
                      <div className="text-xs text-black/60">{industry}</div>
                      {r.company_website && <a href={r.company_website} target="_blank" rel="noreferrer" className="text-xs underline text-blue-700 break-all">{r.company_website}</a>}
                    </td>
                    <td className="p-3 text-xs">
                      <div className="font-semibold">{r.pic_name}{r.pic_position ? ` • ${r.pic_position}` : ''}</div>
                      <div className="text-black/70">{r.pic_email}</div>
                      <div className="text-black/70">{r.pic_phone}</div>
                    </td>
                    <td className="p-3">
                      <span className="inline-block bg-[#F5C518] border border-[#1a1a1a] text-[11px] font-bold px-2 py-0.5 rounded">{BOOTH_LABELS[r.booth_package] || r.booth_package || '-'}</span>
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-1">
                        {(r.sponsorship_interests || []).map((s) => (
                          <span key={s} className="inline-block bg-white border border-[#1a1a1a] text-[10px] font-bold px-1.5 py-0.5 rounded">{s}</span>
                        ))}
                      </div>
                      {r.objectives && <div className="text-[11px] text-black/60 mt-1 max-w-xs">{r.objectives.slice(0, 100)}{r.objectives.length > 100 ? '…' : ''}</div>}
                    </td>
                    <td className="p-3"><OnbCell src="brand" reg={r} status={status} /></td>
                    <td className="p-3">
                      <div className="flex flex-col gap-1.5 min-w-[130px]">
                        {status !== 'approved' && (<button onClick={() => openDecision(r, 'approved')} disabled={status === 'approved'} className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-md border-2 border-emerald-700 bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1">
                          <ThumbsUp className="w-3 h-3"/> Approve
                        </button>)}
                        {status !== 'rejected' && (<button onClick={() => openDecision(r, 'rejected')} disabled={status === 'rejected'} className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-md border-2 border-red-700 bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1">
                          <ThumbsDown className="w-3 h-3"/> Reject
                        </button>)}
                        {status !== 'pending' && (
                          <button onClick={() => openDecision(r, 'pending')} className="text-[10px] text-black/50 hover:text-black underline">reset to pending</button>
                        )}
                        <button onClick={() => openDelete(r)} className="text-[10px] text-red-700 hover:text-red-900 underline underline-offset-2 inline-flex items-center justify-center gap-1 mt-0.5">
                          <Trash2 className="w-3 h-3"/> delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {deleteState && (
        <DeleteDialog state={deleteState} setState={setDeleteState} deleting={deleting} onCancel={closeDelete} onConfirm={confirmDelete} />
      )}

      {decision && (
        <DecisionDialog decision={decision} note={note} setNote={setNote} sendEmail={sendEmail} setSendEmail={setSendEmail} submitting={submitting} onCancel={closeDecision} onConfirm={confirmDecision} corporate />
      )}
    </>
  );
}

function FnbTab({ regs, onReload }) {
  const [filter, setFilter] = useState('all');
  const [decision, setDecision] = useState(null);
  const [note, setNote] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [deleteState, setDeleteState] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const counts = useMemo(() => {
    const c = { all: regs.length, pending: 0, approved: 0, rejected: 0 };
    regs.forEach((r) => { const s = r.status || 'pending'; c[s] = (c[s] || 0) + 1; });
    return c;
  }, [regs]);

  const filtered = useMemo(() => {
    if (filter === 'all') return regs;
    return regs.filter((r) => (r.status || 'pending') === filter);
  }, [regs, filter]);

  const typeOf = (r) => (r.other_fnb_type ? `Other: ${r.other_fnb_type}` : (r.fnb_type || '-'));

  const openDecision = (reg, status) => { setDecision({ reg, status }); setNote(''); setSendEmail(true); };
  const closeDecision = () => { if (!submitting) { setDecision(null); setNote(''); } };
  const confirmDecision = async () => {
    if (!decision) return;
    setSubmitting(true);
    try {
      await api.put(`/fnb/registrations/${decision.reg.id}/status`, { status: decision.status, note, send_email: sendEmail });
      setDecision(null); setNote('');
      onReload && onReload();
    } catch (e) { alert('Gagal update status: ' + (e?.response?.data?.detail || 'error')); }
    finally { setSubmitting(false); }
  };

  const openDelete = (reg) => setDeleteState({ step: 1, reg, ids: [reg.id], password: '', error: '', bulk: false });
  const closeDelete = () => { if (!deleting) setDeleteState(null); };
  const confirmDelete = async () => {
    if (!deleteState) return;
    if (deleteState.step === 1) { setDeleteState((s) => ({ ...s, step: 2, error: '' })); return; }
    setDeleting(true);
    try {
      await api.delete(`/fnb/registrations/${deleteState.ids[0]}`, { data: { password: deleteState.password } });
      setDeleteState(null);
      onReload && onReload();
    } catch (e) {
      setDeleteState((s) => ({ ...s, error: e?.response?.data?.detail || 'Gagal menghapus.' }));
    } finally { setDeleting(false); }
  };

  const buildRows = () => {
    const headers = ['Submitted At','Status','Business','F&B Type','Menu','Instagram','PIC Name','PIC Email','PIC Phone','Power','Notes'];
    const rows = filtered.map(r => [
      r.submitted_at ? parseServerDate(r.submitted_at).toLocaleString() : '',
      r.status || 'pending',
      r.business_name || '',
      typeOf(r),
      r.menu_description || '',
      r.instagram || '',
      r.pic_name || '',
      r.pic_email || '',
      r.pic_phone || '',
      r.power_requirement || '',
      r.notes || '',
    ]);
    return { headers, rows };
  };
  const timestamp = () => { const d = new Date(); const p = (n) => String(n).padStart(2, '0'); return `${d.getFullYear()}${p(d.getMonth()+1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}`; };
  const exportXLSX = () => {
    const { headers, rows } = buildRows();
    const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
    ws['!cols'] = headers.map((h) => ({ wch: Math.max(12, Math.min(40, h.length + 4)) }));
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'F&B');
    XLSX.writeFile(wb, `talesverse_fnb_${filter}_${timestamp()}.xlsx`);
  };
  const exportCSV = () => {
    const { headers, rows } = buildRows();
    const escape = (v) => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const csv = '\uFEFF' + [headers.map(escape).join(','), ...rows.map(r => r.map(escape).join(','))].join('\n');
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob);
    a.download = `talesverse_fnb_${filter}_${timestamp()}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
  };

  return (
    <div data-testid="fnb-tab">
      <div className="bg-white border-2 border-[#1a1a1a] rounded-2xl shadow-[4px_4px_0_#1a1a1a]">
        <div className="flex flex-wrap items-center justify-between gap-3 p-4 border-b-2 border-[#1a1a1a]">
          <h3 className="outline-title text-2xl">F&amp;B Applications</h3>
          <div className="flex flex-wrap items-center gap-2">
            <button onClick={exportXLSX} className="tv-btn px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5" style={{backgroundColor:'#F5C518'}}>
              <FileSpreadsheet className="w-4 h-4"/> Export Excel
            </button>
            <button onClick={exportCSV} className="tv-btn px-3 py-1.5 text-xs font-bold inline-flex items-center gap-1.5">
              <Download className="w-4 h-4"/> Export CSV
            </button>
          </div>
        </div>

        <div className="flex items-center gap-2 px-4 py-3 border-b border-black/10 bg-[#faf7ef] overflow-x-auto">
          <Filter className="w-4 h-4 text-black/50 flex-shrink-0"/>
          {[
            { v: 'all', l: 'All', cls: 'bg-white text-[#1a1a1a] border-[#1a1a1a]' },
            { v: 'pending', l: 'Pending', cls: 'bg-amber-100 text-amber-800 border-amber-500' },
            { v: 'approved', l: 'Approved', cls: 'bg-emerald-100 text-emerald-800 border-emerald-500' },
            { v: 'rejected', l: 'Rejected', cls: 'bg-red-100 text-red-800 border-red-500' },
          ].map((f) => {
            const active = filter === f.v;
            return (
              <button key={f.v} onClick={() => setFilter(f.v)}
                className={`text-[12px] font-bold px-3 py-1.5 rounded-full border-2 whitespace-nowrap ${active ? f.cls + ' shadow-[2px_2px_0_currentColor]' : 'bg-white text-black/60 border-black/20 hover:border-black/40'}`}>
                {f.l} <span className="opacity-70 font-normal">({counts[f.v] || 0})</span>
              </button>
            );
          })}
          <div className="ml-auto text-[11px] font-semibold" style={{ color: '#6b6455' }}>Ekspor mengikuti filter aktif ({filtered.length} baris)</div>
        </div>

        <OnbBar src="fnb" regs={regs} />

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f6f1e7] text-left text-[11px] uppercase tracking-wider text-black/60">
              <tr>
                <th className="p-3">Date</th>
                <th className="p-3">Status</th>
                <th className="p-3">Business / Type</th>
                <th className="p-3">PIC / Contact</th>
                <th className="p-3">Menu / Needs</th>
                <th className="p-3">Onboarding</th>
                <th className="p-3">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.length === 0 && (
                <tr><td colSpan={7} className="p-6 text-center text-black/50">No F&B applications{filter !== 'all' ? ` in "${filter}"` : ''}.</td></tr>
              )}
              {filtered.map((r) => {
                const status = r.status || 'pending';
                return (
                  <tr key={r.id} className="border-t border-black/10 hover:bg-[#fdfbf6] align-top">
                    <td className="p-3 text-xs whitespace-nowrap">{parseServerDate(r.submitted_at).toLocaleString()}</td>
                    <td className="p-3"><StatusPill status={status}/></td>
                    <td className="p-3">
                      <div className="font-semibold">{r.business_name}</div>
                      <div className="text-xs text-black/60">{typeOf(r)}</div>
                      {r.instagram && <a href={r.instagram} target="_blank" rel="noreferrer" className="text-xs underline text-blue-700 break-all">{r.instagram}</a>}
                    </td>
                    <td className="p-3 text-xs">
                      <div className="font-semibold">{r.pic_name}</div>
                      <div className="text-black/70">{r.pic_email}</div>
                      <div className="text-black/70">{r.pic_phone}</div>
                    </td>
                    <td className="p-3 text-xs max-w-xs">
                      {r.menu_description && <div className="text-black/80">{r.menu_description.slice(0, 120)}{r.menu_description.length > 120 ? '…' : ''}</div>}
                      <div className="flex flex-wrap gap-1 mt-1">
                        {r.power_requirement && <span className="inline-block bg-white border border-[#1a1a1a] text-[10px] font-bold px-1.5 py-0.5 rounded">⚡ {r.power_requirement}</span>}
                      </div>
                      {r.notes && <div className="text-[11px] text-black/60 mt-1">{r.notes.slice(0, 80)}{r.notes.length > 80 ? '…' : ''}</div>}
                    </td>
                    <td className="p-3"><OnbCell src="fnb" reg={r} status={status} /></td>
                    <td className="p-3">
                      <div className="flex flex-col gap-1.5 min-w-[130px]">
                        {status !== 'approved' && (<button onClick={() => openDecision(r, 'approved')} disabled={status === 'approved'} className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-md border-2 border-emerald-700 bg-emerald-500 text-white hover:bg-emerald-600 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1">
                          <ThumbsUp className="w-3 h-3"/> Approve
                        </button>)}
                        {status !== 'rejected' && (<button onClick={() => openDecision(r, 'rejected')} disabled={status === 'rejected'} className="text-[11px] font-bold uppercase tracking-wider px-2.5 py-1.5 rounded-md border-2 border-red-700 bg-red-500 text-white hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed inline-flex items-center justify-center gap-1">
                          <ThumbsDown className="w-3 h-3"/> Reject
                        </button>)}
                        {status !== 'pending' && (
                          <button onClick={() => openDecision(r, 'pending')} className="text-[10px] text-black/50 hover:text-black underline">reset to pending</button>
                        )}
                        <button onClick={() => openDelete(r)} className="text-[10px] text-red-700 hover:text-red-900 underline underline-offset-2 inline-flex items-center justify-center gap-1 mt-0.5">
                          <Trash2 className="w-3 h-3"/> delete
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {deleteState && (
        <DeleteDialog state={deleteState} setState={setDeleteState} deleting={deleting} onCancel={closeDelete} onConfirm={confirmDelete} />
      )}

      {decision && (
        <DecisionDialog decision={decision} note={note} setNote={setNote} sendEmail={sendEmail} setSendEmail={setSendEmail} submitting={submitting} onCancel={closeDecision} onConfirm={confirmDecision} corporate />
      )}
    </div>
  );
}

function DeleteDialog({ state, setState, deleting, onCancel, onConfirm }) {
  const { step, reg, ids, password, error, bulk } = state;
  const count = ids?.length || (reg ? 1 : 0);
  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto [&>*]:my-auto bg-black/60 p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl border-2 border-[#1a1a1a] shadow-[8px_8px_0_#1a1a1a] max-w-md w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className="bg-red-600 text-white border-b-2 border-[#1a1a1a] px-5 py-3 flex items-center justify-between">
          <h3 className="font-display font-bold text-lg uppercase tracking-wide inline-flex items-center gap-2">
            <Trash2 className="w-5 h-5"/> {bulk ? `Hapus ${count} Pendaftar` : 'Hapus Data Pendaftar'}
          </h3>
          <button onClick={onCancel} className="hover:bg-white/20 rounded p-1"><X className="w-5 h-5"/></button>
        </div>
        <div className="p-5 space-y-4">
          {bulk ? (
            <div className="bg-[#faf7ef] border border-black/10 rounded-lg p-3 text-sm">
              <div className="font-bold">{count} baris terpilih</div>
              <div className="text-black/70 text-xs">Semua akan dipindahkan ke Trash (24 jam) dan bisa di-restore sebelum expired.</div>
            </div>
          ) : reg ? (
            <div className="bg-[#faf7ef] border border-black/10 rounded-lg p-3 text-sm">
              <div className="font-bold">{reg.brand_name || reg.company_name || reg.business_name || 'Data'}</div>
              <div className="text-black/70 text-xs">{reg.pic_name} • {reg.pic_email}</div>
            </div>
          ) : null}

          {step === 1 && (
            <>
              <div className="rounded-lg border-2 border-red-500 bg-red-50 p-3 text-[13px] text-red-900">
                <div className="font-bold mb-1 inline-flex items-center gap-1.5"><AlertCircle className="w-4 h-4"/> Konfirmasi Langkah 1 dari 2</div>
                {bulk ? <>Total <b>{count} data</b> akan dipindahkan ke <b>Trash</b>.</> : <>Data pendaftar ini akan dipindahkan ke <b>Trash</b>.</>} Bisa di-restore dari tab Trash selama <b>24 jam</b>, setelah itu akan otomatis hilang permanen.
              </div>
              <p className="text-[12px] text-black/60">Klik "Lanjut" untuk masuk ke konfirmasi password.</p>
            </>
          )}

          {step === 2 && (
            <>
              <div className="rounded-lg border-2 border-red-500 bg-red-50 p-3 text-[13px] text-red-900">
                <div className="font-bold mb-1 inline-flex items-center gap-1.5"><Lock className="w-4 h-4"/> Konfirmasi Langkah 2 dari 2</div>
                Masukkan <b>password admin</b> untuk mengonfirmasi.
              </div>
              <label className="block">
                <div className="text-[11px] font-bold uppercase tracking-wider text-black/70 mb-1.5">Admin Password</div>
                <input
                  type="password"
                  autoFocus
                  value={password}
                  onChange={(e) => setState((s) => ({ ...s, password: e.target.value, error: '' }))}
                  onKeyDown={(e) => { if (e.key === 'Enter' && password) onConfirm(); }}
                  placeholder="••••••••"
                  className="w-full h-11 px-3 rounded-lg border-2 border-[#1a1a1a] bg-white text-[14px] font-medium focus:outline-none focus:ring-2 focus:ring-red-500"
                />
                {error && <div className="text-red-600 text-xs font-semibold mt-1.5">{error}</div>}
              </label>
            </>
          )}
        </div>

        <div className="p-4 border-t-2 border-[#1a1a1a] flex items-center justify-end gap-2 bg-[#faf7ef]">
          <button onClick={onCancel} disabled={deleting} className="text-sm font-semibold px-4 py-2 rounded-md border-2 border-black/20 hover:border-black/40 disabled:opacity-50">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={deleting || (step === 2 && !password)}
            className="text-sm font-bold uppercase px-4 py-2 rounded-md border-2 border-red-800 bg-red-600 text-white hover:bg-red-700 inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {deleting ? <Loader2 className="w-4 h-4 animate-spin"/> : <Trash2 className="w-4 h-4"/>}
            {step === 1 ? 'Lanjut →' : (bulk ? `Hapus ${count}` : 'Hapus')}
          </button>
        </div>
      </div>
    </div>
  );
}

function StatusPill({ status }) {
  const cfg = {
    approved: { bg: 'bg-emerald-100', color: 'text-emerald-800', border: 'border-emerald-500', label: 'APPROVED' },
    rejected: { bg: 'bg-red-100', color: 'text-red-800', border: 'border-red-500', label: 'REJECTED' },
    pending: { bg: 'bg-amber-100', color: 'text-amber-800', border: 'border-amber-500', label: 'PENDING' },
  }[status] || { bg: 'bg-amber-100', color: 'text-amber-800', border: 'border-amber-500', label: 'PENDING' };
  return (
    <span className={`inline-block ${cfg.bg} ${cfg.color} border ${cfg.border} text-[10px] font-black px-2 py-0.5 rounded-full tracking-wider`}>
      {cfg.label}
    </span>
  );
}

function DecisionDialog({ decision, note, setNote, sendEmail, setSendEmail, submitting, onCancel, onConfirm, corporate = false }) {
  const { reg, status } = decision;
  const isApprove = status === 'approved';
  const isReject = status === 'rejected';
  const isReset = status === 'pending';

  const title = corporate
    ? (isApprove ? 'Approve Partnership' : isReject ? 'Reject Partnership' : 'Reset to Pending')
    : (isApprove ? 'Approve Application' : isReject ? 'Reject Application' : 'Reset to Pending');
  const accent = isApprove ? 'bg-emerald-500 border-emerald-700' : isReject ? 'bg-red-500 border-red-700' : 'bg-amber-500 border-amber-700';
  const noteLabel = isApprove
    ? (corporate ? 'Catatan untuk Partner (muncul di email)' : 'Catatan Personal (opsional)')
    : isReject
      ? (corporate ? 'Feedback / Alasan (muncul di email partner)' : 'Alasan/Feedback (opsional, muncul di email pendaftar)')
      : 'Catatan Internal (opsional)';
  const notePlaceholder = corporate
    ? (isApprove
        ? 'Contoh: Slot booth Gold Zone A-05 dikonfirmasi. PIC finance kami: Rita (0812-xxx). Invoice akan dikirim H+1…'
        : isReject
          ? 'Contoh: Kuota Gold Booth sudah full untuk kategori F&B, kami akan reach-out untuk edisi berikutnya…'
          : 'Alasan reset…')
    : (isApprove
        ? 'Contoh: Booth slot A-12 sudah disiapkan. Pembayaran via transfer BCA…'
        : isReject
          ? 'Contoh: Kategori sudah penuh, encouraging kalimat…'
          : 'Alasan reset…');

  const displayName = reg.brand_name || reg.company_name || 'Data';
  const displayMeta = corporate
    ? `${reg.pic_name || ''}${reg.pic_position ? ' • ' + reg.pic_position : ''}`
    : (reg.pic_name || '');
  const displayCat = corporate
    ? `${reg.industry || reg.other_industry || '-'} • ${(reg.booth_package || '').toUpperCase()}`
    : (reg.category || (reg.categories || []).join(', ') || '-');

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto [&>*]:my-auto bg-black/60 p-4" onClick={onCancel}>
      <div className="bg-white rounded-2xl border-2 border-[#1a1a1a] shadow-[8px_8px_0_#1a1a1a] max-w-lg w-full overflow-hidden" onClick={(e) => e.stopPropagation()}>
        <div className={`${accent} text-white border-b-2 border-[#1a1a1a] px-5 py-3 flex items-center justify-between`}>
          <h3 className="font-display font-bold text-lg uppercase tracking-wide inline-flex items-center gap-2">
            {isApprove && <ThumbsUp className="w-5 h-5"/>}
            {isReject && <ThumbsDown className="w-5 h-5"/>}
            {isReset && <RefreshCw className="w-5 h-5"/>}
            {title}
          </h3>
          <button onClick={onCancel} className="hover:bg-white/20 rounded p-1"><X className="w-5 h-5"/></button>
        </div>

        <div className="p-5 space-y-4">
          <div className="bg-[#faf7ef] border border-black/10 rounded-lg p-3 text-sm">
            <div className="font-bold">{displayName}</div>
            <div className="text-black/70 text-xs">{displayMeta} • {reg.pic_email}</div>
            <div className="text-black/70 text-xs mt-0.5">{displayCat}</div>
          </div>

          <label className="block">
            <div className="text-[11px] font-bold uppercase tracking-wider text-black/70 mb-1.5">{noteLabel}</div>
            <textarea
              rows={4}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={notePlaceholder}
              className="w-full px-3 py-2 rounded-lg border-2 border-[#1a1a1a] bg-white text-[13px] font-medium resize-none focus:outline-none focus:ring-2 focus:ring-[#E63329]"
            />
          </label>

          {!isReset && (
            <label className="flex items-center gap-2 text-sm cursor-pointer select-none">
              <input
                type="checkbox"
                checked={sendEmail}
                onChange={(e) => setSendEmail(e.target.checked)}
                className="w-4 h-4 accent-[#E63329]"
              />
              <span>Kirim email keputusan otomatis ke <b>{reg.pic_email}</b></span>
            </label>
          )}

          {isApprove && sendEmail && !corporate && (
            <div className="bg-emerald-50 border-2 border-emerald-500 rounded-lg p-3 text-[12px] text-emerald-900">
              Pendaftar akan menerima email <b>🎉 Selamat!</b> berisi info payment booth Rp 2 juta + next steps.
            </div>
          )}
          {isApprove && sendEmail && corporate && (
            <div className="bg-emerald-50 border-2 border-emerald-500 rounded-lg p-3 text-[12px] text-emerald-900">
              Partner akan menerima email <b>Partnership Confirmed</b> berisi paket booth, timeline invoice (DP 50%, pelunasan H-30), 5-step timeline, dan request PIC finance + NPWP.
            </div>
          )}
          {isReject && sendEmail && !corporate && (
            <div className="bg-red-50 border-2 border-red-500 rounded-lg p-3 text-[12px] text-red-900">
              Pendaftar akan menerima email <b>rejection sopan</b> — bukan penolakan karya, tapi karena kapasitas venue.
            </div>
          )}
          {isReject && sendEmail && corporate && (
            <div className="bg-red-50 border-2 border-red-500 rounded-lg p-3 text-[12px] text-red-900">
              Partner akan menerima email <b>Partnership Update</b> yang sopan, menekankan bukan penilaian brand + open peluang partnership berikutnya.
            </div>
          )}
        </div>

        <div className="p-4 border-t-2 border-[#1a1a1a] flex items-center justify-end gap-2 bg-[#faf7ef]">
          <button onClick={onCancel} disabled={submitting} className="text-sm font-semibold px-4 py-2 rounded-md border-2 border-black/20 hover:border-black/40 disabled:opacity-50">Cancel</button>
          <button
            onClick={onConfirm}
            disabled={submitting}
            className={`text-sm font-bold uppercase px-4 py-2 rounded-md border-2 text-white inline-flex items-center gap-2 disabled:opacity-60 ${accent}`}
          >
            {submitting ? <Loader2 className="w-4 h-4 animate-spin"/> : (isApprove ? <ThumbsUp className="w-4 h-4"/> : isReject ? <ThumbsDown className="w-4 h-4"/> : <RefreshCw className="w-4 h-4"/>)}
            Confirm {isApprove ? 'Approval' : isReject ? 'Rejection' : 'Reset'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PixelsTab({ pixels, setPixels, save, savedMsg }) {
  const upd = (k, v) => setPixels((s) => ({ ...s, [k]: v }));
  return (
    <div className="space-y-5 max-w-3xl">
      <div className="bg-white border-2 border-[#1a1a1a] rounded-2xl p-5 shadow-[4px_4px_0_#1a1a1a]">
        <h3 className="outline-title text-2xl mb-1">Tracking Pixels</h3>
        <p className="text-sm text-black/60 mb-4">Paste your pixel/measurement IDs. They will be injected on the public Linktree page.</p>

        <div className="grid md:grid-cols-2 gap-4">
          <PixelField label="Meta (Facebook) Pixel ID" placeholder="e.g. 1234567890" value={pixels.meta_pixel_id} onChange={(v)=>upd('meta_pixel_id',v)} />
          <PixelField label="Google Analytics ID (GA4)" placeholder="e.g. G-XXXXXXX" value={pixels.ga_measurement_id} onChange={(v)=>upd('ga_measurement_id',v)} />
          <PixelField label="Google Tag Manager ID" placeholder="e.g. GTM-XXXXXX" value={pixels.gtm_id} onChange={(v)=>upd('gtm_id',v)} />
          <PixelField label="TikTok Pixel ID" placeholder="e.g. CXXXXXXXXXXXXXXXXX" value={pixels.tiktok_pixel_id} onChange={(v)=>upd('tiktok_pixel_id',v)} />
        </div>

        <div className="flex items-center gap-3 mt-5">
          <button onClick={save} className="tv-btn px-4 py-2 font-display font-bold inline-flex items-center gap-2" style={{backgroundColor:'#F5C518'}}>
            <Save className="w-4 h-4"/> Save
          </button>
          {savedMsg && <span className="text-emerald-600 text-sm font-semibold">{savedMsg}</span>}
        </div>
      </div>

      <div className="bg-white border-2 border-[#1a1a1a] rounded-2xl p-5 shadow-[4px_4px_0_#1a1a1a] text-sm">
        <h4 className="font-display font-bold text-lg mb-1">Google Sheets Sync</h4>
        <p className="text-black/70">Creator registrations are stored in the database. To also sync to Google Sheets, set <code className="bg-black/5 px-1 rounded">GOOGLE_SHEET_ID</code> and <code className="bg-black/5 px-1 rounded">GOOGLE_SA_JSON</code> in the backend <code>.env</code>. Once set, new submissions will show <b className="text-emerald-600">SYNCED</b> in the Registrations tab.</p>
      </div>
    </div>
  );
}

function PixelField({ label, value, onChange, placeholder }) {
  return (
    <label className="block">
      <div className="text-[11px] font-bold uppercase tracking-wider text-black/70 mb-1.5">{label}</div>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="w-full h-10 px-3 rounded-lg border-2 border-[#1a1a1a] bg-white text-[13px] font-medium focus:outline-none focus:ring-2 focus:ring-[#E63329]"
      />
    </label>
  );
}

function EmailTab({ status, onRefresh }) {
  const [testTo, setTestTo] = useState('');
  const [sending, setSending] = useState(false);
  const [result, setResult] = useState(null);

  // Admin notify state
  const [notify, setNotify] = useState({ emails: '', dashboard_url: '' });
  const [notifyLoaded, setNotifyLoaded] = useState(false);
  const [notifySaving, setNotifySaving] = useState(false);
  const [notifyMsg, setNotifyMsg] = useState('');
  const [notifyTesting, setNotifyTesting] = useState(false);
  const [notifyTestResult, setNotifyTestResult] = useState(null);

  useEffect(() => {
    api.get('/notify/settings').then(({data}) => {
      setNotify({ emails: data.emails || '', dashboard_url: data.dashboard_url || '' });
      setNotifyLoaded(true);
    }).catch(() => setNotifyLoaded(true));
  }, []);

  const saveNotify = async () => {
    setNotifySaving(true); setNotifyMsg('');
    try {
      await api.put('/notify/settings', notify);
      setNotifyMsg('Tersimpan!');
      setTimeout(() => setNotifyMsg(''), 2500);
    } catch {
      setNotifyMsg('Gagal menyimpan');
    } finally { setNotifySaving(false); }
  };

  const testAdminNotify = async () => {
    setNotifyTesting(true); setNotifyTestResult(null);
    try {
      const { data } = await api.post('/notify/test', {});
      setNotifyTestResult({ ok: data.ok, msg: `Terkirim ke: ${data.sent_to.join(', ') || '-'}${data.failed?.length ? ` — Gagal: ${data.failed.join(', ')}` : ''}` });
    } catch (e) {
      setNotifyTestResult({ ok: false, msg: e?.response?.data?.detail || 'Gagal kirim.' });
    } finally { setNotifyTesting(false); }
  };

  const send = async () => {
    if (!testTo.trim()) return;
    setSending(true); setResult(null);
    try {
      await api.post('/email/test', { to: testTo.trim() });
      setResult({ ok: true, msg: `Test email berhasil dikirim ke ${testTo}` });
    } catch (e) {
      setResult({ ok: false, msg: e?.response?.data?.detail || 'Gagal mengirim test email.' });
    } finally { setSending(false); }
  };

  const configured = status?.configured;

  return (
    <div className="space-y-5 max-w-3xl">
      <div className="bg-white border-2 border-[#1a1a1a] rounded-2xl p-5 shadow-[4px_4px_0_#1a1a1a]">
        <h3 className="outline-title text-2xl mb-1">Email Confirmation</h3>
        <p className="text-sm text-black/60 mb-4">Setiap pendaftar otomatis menerima email konfirmasi ber-branding TalesVerse.</p>

        <div className={`rounded-lg border-2 p-4 flex items-start gap-3 ${configured ? 'bg-emerald-50 border-emerald-500' : 'bg-amber-50 border-amber-500'}`}>
          {configured
            ? <CheckCircle2 className="w-5 h-5 text-emerald-600 flex-shrink-0 mt-0.5" />
            : <AlertCircle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />}
          <div className="text-sm">
            <div className="font-bold">
              {configured ? 'SMTP AKTIF — email otomatis siap terkirim' : 'SMTP BELUM DIKONFIGURASI'}
            </div>
            {!configured && (
              <div className="mt-1 text-[13px] text-amber-900">
                Isi variabel SMTP di <code className="bg-white px-1 rounded">/app/backend/.env</code> lalu restart backend.
              </div>
            )}
          </div>
        </div>

        <div className="grid md:grid-cols-2 gap-3 mt-4 text-sm">
          <ConfigRow label="SMTP Host" value={status?.smtp_host || '—'} />
          <ConfigRow label="SMTP Port" value={status?.smtp_port || '—'} />
          <ConfigRow label="From Email" value={status?.email_from || '—'} />
          <ConfigRow label="From Name" value={status?.email_from_name || '—'} />
          <ConfigRow label="Reply-To" value={status?.email_reply_to || '(sama dengan From)'} full />
        </div>

        <button onClick={onRefresh} className="mt-3 text-xs underline underline-offset-4 text-black/60">Refresh status</button>
      </div>

      <div className="bg-white border-2 border-[#1a1a1a] rounded-2xl p-5 shadow-[4px_4px_0_#1a1a1a]">
        <h4 className="font-display font-bold text-lg mb-1 inline-flex items-center gap-2"><Bell className="w-4 h-4"/> Notifikasi Admin</h4>
        <p className="text-sm text-black/60 mb-3">Kirim ping ke email panitia setiap kali ada pendaftar baru.</p>

        <label className="block mb-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-black/70 mb-1.5">Admin Email(s) <span className="text-black/40 normal-case tracking-normal">— pisahkan koma jika lebih dari 1</span></div>
          <input
            value={notify.emails}
            onChange={(e) => setNotify((s) => ({ ...s, emails: e.target.value }))}
            placeholder="partnership@luckytalesgroup.com, ops@…"
            className="w-full h-11 px-3 rounded-lg border-2 border-[#1a1a1a] bg-white text-[14px] font-medium focus:outline-none focus:ring-2 focus:ring-[#E63329]"
          />
        </label>

        <label className="block mb-3">
          <div className="text-[11px] font-bold uppercase tracking-wider text-black/70 mb-1.5">Dashboard URL <span className="text-black/40 normal-case tracking-normal">— muncul di tombol "Open Dashboard"</span></div>
          <input
            value={notify.dashboard_url}
            onChange={(e) => setNotify((s) => ({ ...s, dashboard_url: e.target.value }))}
            placeholder="https://linktree-clone-22.emergent.host/dashboard"
            className="w-full h-11 px-3 rounded-lg border-2 border-[#1a1a1a] bg-white text-[14px] font-medium focus:outline-none focus:ring-2 focus:ring-[#E63329]"
          />
        </label>

        <div className="flex flex-wrap items-center gap-2">
          <button
            onClick={saveNotify}
            disabled={notifySaving || !notifyLoaded}
            className="tv-btn px-4 py-2 font-display font-bold inline-flex items-center gap-2 disabled:opacity-60"
            style={{ backgroundColor: '#F5C518' }}
          >
            <Save className="w-4 h-4"/> Save
          </button>
          <button
            onClick={testAdminNotify}
            disabled={notifyTesting || !configured}
            className="tv-btn px-4 py-2 font-display font-bold inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
          >
            {notifyTesting ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
            Send Preview to Admin
          </button>
          {notifyMsg && <span className="text-emerald-600 text-sm font-semibold">{notifyMsg}</span>}
        </div>
        {notifyTestResult && (
          <div className={`mt-3 text-sm font-semibold ${notifyTestResult.ok ? 'text-emerald-600' : 'text-red-600'}`}>{notifyTestResult.msg}</div>
        )}
      </div>

      <div className="bg-white border-2 border-[#1a1a1a] rounded-2xl p-5 shadow-[4px_4px_0_#1a1a1a]">
        <h4 className="font-display font-bold text-lg mb-1 inline-flex items-center gap-2"><Mail className="w-4 h-4"/> Kirim Test Email</h4>
        <p className="text-sm text-black/60 mb-3">Preview email konfirmasi pendaftar ke alamat Anda.</p>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            type="email"
            value={testTo}
            onChange={(e) => setTestTo(e.target.value)}
            placeholder="you@example.com"
            className="flex-1 h-11 px-3 rounded-lg border-2 border-[#1a1a1a] bg-white text-[14px] font-medium focus:outline-none focus:ring-2 focus:ring-[#E63329]"
          />
          <button
            onClick={send}
            disabled={sending || !testTo.trim() || !configured}
            className="tv-btn px-4 py-2 font-display font-bold inline-flex items-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed"
            style={{ backgroundColor: '#F5C518' }}
          >
            {sending ? <Loader2 className="w-4 h-4 animate-spin"/> : <Send className="w-4 h-4"/>}
            Send
          </button>
        </div>
        {result && (
          <div className={`mt-3 text-sm font-semibold ${result.ok ? 'text-emerald-600' : 'text-red-600'}`}>{result.msg}</div>
        )}
      </div>
    </div>
  );
}

function ConfigRow({ label, value, full }) {
  return (
    <div className={`${full ? 'md:col-span-2' : ''} flex items-center justify-between gap-3 bg-[#f6f1e7] border border-black/10 rounded-lg px-3 py-2`}>
      <span className="text-[11px] font-bold uppercase tracking-wider text-black/60">{label}</span>
      <span className="text-[13px] font-mono font-semibold truncate">{value}</span>
    </div>
  );
}

const EMPTY_ENTRY = { name: '', role: '', type: 'creator', social_link: '', image_url: '', image_position: '50% 50%', image_scale: 1, image_bg: '', order: 0, active: true };

function LineupTab({ entries, onReload }) {
  const [editing, setEditing] = useState(null); // null | entry object (with id) | 'new'
  const [form, setForm] = useState(EMPTY_ENTRY);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [deleteId, setDeleteId] = useState(null);
  const [items, setItems] = useState(entries);
  const [dragIdx, setDragIdx] = useState(null);
  const [overIdx, setOverIdx] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkText, setBulkText] = useState('');
  const [bulkType, setBulkType] = useState('creator');
  const [bulkSaving, setBulkSaving] = useState(false);
  const [bulkErr, setBulkErr] = useState('');
  const [deleteAllOpen, setDeleteAllOpen] = useState(false);
  const [deletingAll, setDeletingAll] = useState(false);
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(0);
  useEffect(() => { setPage(0); }, [search]);
  const [posEditing, setPosEditing] = useState(null);
  const [pos, setPos] = useState({ x: 50, y: 50 });
  const [posScale, setPosScale] = useState(1);
  const [posBg, setPosBg] = useState('');
  const [showShape, setShowShape] = useState(true);
  const [pickMode, setPickMode] = useState(false);
  const dragStartRef = useRef(null);
  const posImgRef = useRef(null);
  const [posSaving, setPosSaving] = useState(false);
  const frameRef = useRef(null);
  const draggingRef = useRef(false);
  useEffect(() => {
    const el = frameRef.current;
    if (!el) return undefined;
    const onWheel = (ev) => {
      ev.preventDefault();
      ev.stopPropagation();
      setPosScale((s) => Math.round(Math.min(3, Math.max(0.3, s - ev.deltaY * 0.0015)) * 100) / 100);
    };
    el.addEventListener('wheel', onWheel, { passive: false });
    return () => el.removeEventListener('wheel', onWheel);
  }, [posEditing]);

  const openPos = (e) => {
    const m = (e.image_position || '50% 50%').match(/([\d.]+)%\s+([\d.]+)%/);
    setPos({ x: m ? Math.round(+m[1]) : 50, y: m ? Math.round(+m[2]) : 50 });
    setPosScale(Number(e.image_scale) || 1);
    setPosBg(e.image_bg || '');
    setPosEditing(e);
  };
  const posFromEvent = (ev) => {
    if (!frameRef.current) return;
    const r = frameRef.current.getBoundingClientRect();
    if (ev.type === 'pointerdown' || !dragStartRef.current) {
      dragStartRef.current = { cx: ev.clientX, cy: ev.clientY, x: pos.x, y: pos.y };
      return;
    }
    const st = dragStartRef.current;
    const dir = posScale < 1 ? 1 : -1;
    const k = posScale < 1 ? 100 / Math.max(0.05, 1 - posScale) : 100 / Math.max(1, posScale);
    const nx = Math.min(100, Math.max(0, st.x + dir * ((ev.clientX - st.cx) / r.width) * k));
    const ny = Math.min(100, Math.max(0, st.y + dir * ((ev.clientY - st.cy) / r.height) * k));
    setPos({ x: Math.round(nx * 10) / 10, y: Math.round(ny * 10) / 10 });
  };
  const sampleAt = (ev) => {
    const img = posImgRef.current, fr = frameRef.current;
    if (!img || !fr || !img.naturalWidth) return;
    const r = fr.getBoundingClientRect();
    const fx = ev.clientX - r.left, fy = ev.clientY - r.top;
    const nr = img.naturalWidth / img.naturalHeight;
    const W = nr >= 1 ? r.height * nr : r.width, H = nr >= 1 ? r.height : r.width / nr;
    const ox = (r.width - W) * pos.x / 100, oy = (r.height - H) * pos.y / 100;
    const px = r.width * pos.x / 100, py = r.height * pos.y / 100;
    const x = px + (fx - px) / posScale, y = py + (fy - py) / posScale;
    const ix = (x - ox) / W, iy = (y - oy) / H;
    if (ix < 0 || ix > 1 || iy < 0 || iy > 1) { setPickMode(false); return; }
    try {
      const c = document.createElement('canvas'); c.width = 1; c.height = 1;
      const cx = c.getContext('2d');
      cx.drawImage(img, Math.floor(ix * img.naturalWidth), Math.floor(iy * img.naturalHeight), 1, 1, 0, 0, 1, 1);
      const p = cx.getImageData(0, 0, 1, 1).data;
      setPosBg('#' + [p[0], p[1], p[2]].map((v) => v.toString(16).padStart(2, '0')).join(''));
    } catch (e) { /* gambar beda domain, tidak bisa diambil warnanya */ }
    setPickMode(false);
  };
  const pickColor = async () => {
    if (window.EyeDropper) {
      try { const res = await new window.EyeDropper().open(); if (res && res.sRGBHex) setPosBg(res.sRGBHex); } catch (e) { /* dibatalkan */ }
      return;
    }
    setPickMode((v) => !v);
  };
  const savePos = async () => {
    if (!posEditing) return;
    setPosSaving(true);
    const value = `${pos.x}% ${pos.y}%`;
    try {
      await api.put(`/lineup/${posEditing.id}`, { image_position: value, image_scale: posScale, image_bg: posBg });
      setItems((prev) => prev.map((it) => (it.id === posEditing.id ? { ...it, image_position: value, image_scale: posScale, image_bg: posBg } : it)));
      setPosEditing(null);
      onReload();
    } finally { setPosSaving(false); }
  };

  const bulkNames = bulkText.split('\n').map((s) => s.trim()).filter(Boolean);

  const submitBulk = async () => {
    if (bulkNames.length === 0) { setBulkErr('Masukkan minimal satu nama.'); return; }
    setBulkSaving(true); setBulkErr('');
    try {
      const { data } = await api.post('/lineup/bulk', { names: bulkNames, type: bulkType });
      setBulkOpen(false); setBulkText('');
      setSyncMsg(`${data.created} tenant ditambahkan ke Line Up.`);
      setTimeout(() => setSyncMsg(''), 4000);
      onReload();
    } catch (err) { setBulkErr(err?.response?.data?.detail || 'Gagal menambahkan.'); }
    finally { setBulkSaving(false); }
  };

  const doDeleteAll = async () => {
    setDeletingAll(true);
    try {
      const { data } = await api.delete('/lineup');
      setDeleteAllOpen(false);
      setSyncMsg(`${data.deleted} entri Line Up dihapus.`);
      setTimeout(() => setSyncMsg(''), 4000);
      onReload();
    } catch (err) { /* ignore */ }
    finally { setDeletingAll(false); }
  };

  useEffect(() => { setItems(entries); }, [entries]);

  const persistOrder = async (ordered) => {
    try { await api.post('/lineup/reorder', { ids: ordered.map((e) => e.id) }); }
    catch (err) { onReload(); }
  };

  const onDragStart = (idx) => setDragIdx(idx);
  const onDragOver = (ev, idx) => { ev.preventDefault(); if (idx !== overIdx) setOverIdx(idx); };
  const onDrop = (idx) => {
    if (dragIdx === null || dragIdx === idx) { setDragIdx(null); setOverIdx(null); return; }
    const next = [...items];
    const [moved] = next.splice(dragIdx, 1);
    next.splice(idx, 0, moved);
    setItems(next);
    setDragIdx(null); setOverIdx(null);
    persistOrder(next);
  };

  const syncApproved = async () => {
    setSyncing(true); setSyncMsg('');
    try {
      const { data } = await api.post('/lineup/sync-approved');
      setSyncMsg(data.created > 0 ? `${data.created} creator ter-approve ditambahkan ke Line Up.` : 'Semua creator ter-approve sudah ada di Line Up.');
      onReload();
    } catch (err) {
      setSyncMsg(err?.response?.data?.detail || 'Gagal sinkronisasi.');
    } finally { setSyncing(false); setTimeout(() => setSyncMsg(''), 4000); }
  };

  const openNew = () => { setForm({ ...EMPTY_ENTRY, order: (entries.length ? Math.max(...entries.map(e => e.order || 0)) + 1 : 1) }); setEditing('new'); setError(''); };
  const openEdit = (e) => { setForm({ ...EMPTY_ENTRY, ...e }); setEditing(e); setError(''); };
  const close = () => { setEditing(null); setForm(EMPTY_ENTRY); setError(''); };

  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/lineup/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      upd('image_url', data.image_url);
    } catch (err) {
      setError(err?.response?.data?.detail || 'Gagal mengunggah gambar.');
    } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { setError('Nama wajib diisi.'); return; }
    setSaving(true); setError('');
    try {
      const payload = { name: form.name, role: form.role, type: form.type, social_link: form.social_link, image_url: form.image_url, image_position: form.image_position || '50% 50%', order: Number(form.order) || 0, active: !!form.active };
      if (editing === 'new') await api.post('/lineup', payload);
      else await api.put(`/lineup/${editing.id}`, payload);
      close();
      onReload();
    } catch (err) {
      setError(err?.response?.data?.detail || 'Gagal menyimpan.');
    } finally { setSaving(false); }
  };

  const toggleActive = async (e) => {
    await api.put(`/lineup/${e.id}`, { active: !e.active });
    onReload();
  };

  const doDelete = async () => {
    if (!deleteId) return;
    await api.delete(`/lineup/${deleteId}`);
    setDeleteId(null);
    onReload();
  };

  const LINEUP_PAGE_SIZE = 10;
  const q = search.trim().toLowerCase();
  const filtered = q ? items.filter((e) => (e.name || '').toLowerCase().includes(q)) : items;
  const pageCount = Math.max(1, Math.ceil(filtered.length / LINEUP_PAGE_SIZE));
  const safePage = Math.min(page, pageCount - 1);
  const visible = filtered.slice(safePage * LINEUP_PAGE_SIZE, safePage * LINEUP_PAGE_SIZE + LINEUP_PAGE_SIZE);
  const dragEnabled = !q;

  return (
    <div className="space-y-5" data-testid="lineup-tab">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h3 className="outline-title text-2xl">Line Up</h3>
          <p className="text-sm text-black/60">Kelola daftar creator & brand yang tampil di homepage. Seret kartu untuk mengubah urutan.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <button onClick={() => { setBulkOpen(true); setBulkText(''); setBulkErr(''); setBulkType('creator'); }} data-testid="lineup-bulk-btn"
            className="bg-white text-[#1a1a1a] px-4 py-2 rounded-lg text-sm font-bold inline-flex items-center gap-1.5 hover:bg-[#f6f1e7] border-2 border-[#1a1a1a] shadow-[3px_3px_0_#1a1a1a]">
            <Upload className="w-4 h-4"/> Bulk Upload
          </button>
          <button onClick={syncApproved} disabled={syncing} data-testid="lineup-sync-btn"
            className="bg-white text-[#1a1a1a] px-4 py-2 rounded-lg text-sm font-bold inline-flex items-center gap-1.5 hover:bg-[#f6f1e7] border-2 border-[#1a1a1a] shadow-[3px_3px_0_#1a1a1a] disabled:opacity-60">
            {syncing ? <Loader2 className="w-4 h-4 animate-spin"/> : <RefreshCw className="w-4 h-4"/>} Tarik Creator Approved
          </button>
          <button onClick={openNew} data-testid="lineup-add-btn"
            className="bg-[#E63329] text-white px-4 py-2 rounded-lg text-sm font-bold inline-flex items-center gap-1.5 hover:bg-[#c92a21] border-2 border-[#1a1a1a] shadow-[3px_3px_0_#1a1a1a]">
            <Plus className="w-4 h-4"/> Tambah
          </button>
          <button onClick={() => setDeleteAllOpen(true)} disabled={!entries.length} data-testid="lineup-delete-all-btn"
            className="bg-white text-red-600 px-4 py-2 rounded-lg text-sm font-bold inline-flex items-center gap-1.5 hover:bg-red-50 border-2 border-red-500 shadow-[3px_3px_0_#ef4444] disabled:opacity-40 disabled:cursor-not-allowed">
            <Trash2 className="w-4 h-4"/> Delete All
          </button>
        </div>
      </div>

      {syncMsg && <div className="text-sm font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2" data-testid="lineup-sync-msg">{syncMsg}</div>}

      <div className="flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-[220px] max-w-sm">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-white/40" />
          <input data-testid="lineup-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Cari nama creator / brand…"
            className="w-full bg-white border-2 border-[#1a1a1a] rounded-lg pl-9 pr-3 py-2 text-sm focus:outline-none" />
        </div>
        <div className="text-xs text-white/50 font-semibold">{filtered.length} entri{q && ` (filter: "${search}")`}</div>
      </div>

      {items.length === 0 && (
        <div className="bg-white border-2 border-dashed border-black/20 rounded-2xl p-10 text-center text-black/50 text-sm">
          Belum ada entri Line Up. Klik <b>Tambah</b> untuk menambahkan creator / brand pertama, atau <b>Tarik Creator Approved</b>.
        </div>
      )}
      {items.length > 0 && filtered.length === 0 && (
        <div className="bg-white border-2 border-dashed border-black/20 rounded-2xl p-10 text-center text-black/50 text-sm" data-testid="lineup-no-results">
          Tidak ada entri dengan nama "<b>{search}</b>".
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
        {visible.map((e) => {
          const idx = items.findIndex((x) => x.id === e.id);
          return (
          <div
            key={e.id}
            draggable={dragEnabled}
            onDragStart={() => dragEnabled && onDragStart(idx)}
            onDragOver={(ev) => dragEnabled && onDragOver(ev, idx)}
            onDrop={() => dragEnabled && onDrop(idx)}
            onDragEnd={() => { setDragIdx(null); setOverIdx(null); }}
            data-testid={`lineup-card-${e.id}`}
            className={`bg-white border-2 rounded-2xl overflow-hidden shadow-[4px_4px_0_#1a1a1a] transition-all ${dragEnabled ? 'cursor-move' : ''} ${e.active ? '' : 'opacity-60'} ${overIdx === idx && dragIdx !== null ? 'border-[#E63329] ring-2 ring-[#E63329]' : 'border-[#1a1a1a]'} ${dragIdx === idx ? 'opacity-40' : ''}`}
          >
            <div className="aspect-square bg-[#f6f1e7] overflow-hidden relative" style={{ background: e.image_bg || undefined }}>
              {e.image_url
                ? <img src={resolveImageUrl(e.image_url)} alt={e.name} className="w-full h-full object-cover" style={{ objectPosition: e.image_position || '50% 50%', transform: `scale(${Number(e.image_scale) || 1})`, transformOrigin: e.image_position || '50% 50%' }} draggable={false}/>
                : <div className="w-full h-full flex items-center justify-center text-black/30 text-xs">No image</div>}
              {dragEnabled && <div className="absolute top-2 left-2 w-6 h-6 rounded-md bg-white/90 border border-[#1a1a1a] flex items-center justify-center"><GripVertical className="w-3.5 h-3.5 text-black/50"/></div>}
              {e.image_url && (
                <button onClick={() => openPos(e)} data-testid={`lineup-pos-${e.id}`} title="Atur posisi gambar"
                  className="absolute bottom-2 right-2 inline-flex items-center gap-1 text-[10px] font-bold px-2 py-1 rounded-md bg-black/70 text-white hover:bg-[#E63329] transition-colors"><Move className="w-3 h-3"/> Posisi</button>
              )}
            </div>
            <div className="p-3">
              <div className="flex items-center gap-1.5 mb-1 flex-wrap">
                <span className={`text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded ${e.type === 'brand' ? 'bg-[#1a1a1a] text-white' : 'bg-[#F5C518] text-[#1a1a1a]'}`}>{e.type}</span>
                {e.source_reg_id && <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-emerald-100 text-emerald-700">approved</span>}
                {!e.active && <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-black/10 text-black/60">hidden</span>}
                <span className="ml-auto text-[10px] text-black/40">#{e.order}</span>
              </div>
              <div className="font-display text-[15px] leading-tight truncate">{e.name}</div>
              <div className="text-[12px] text-black/60 truncate">{e.role || '—'}</div>
              <div className="flex items-center gap-1.5 mt-3">
                <button onClick={() => openEdit(e)} data-testid={`lineup-edit-${e.id}`} className="flex-1 text-xs font-semibold py-1.5 rounded-md border-2 border-[#1a1a1a] hover:bg-[#f6f1e7] inline-flex items-center justify-center gap-1"><Pencil className="w-3.5 h-3.5"/> Edit</button>
                <button onClick={() => toggleActive(e)} title={e.active ? 'Sembunyikan' : 'Tampilkan'} className="px-2 py-1.5 rounded-md border-2 border-[#1a1a1a] hover:bg-[#f6f1e7]">{e.active ? <Eye className="w-3.5 h-3.5"/> : <EyeOff className="w-3.5 h-3.5"/>}</button>
                <button onClick={() => setDeleteId(e.id)} data-testid={`lineup-delete-${e.id}`} className="px-2 py-1.5 rounded-md border-2 border-red-500 text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5"/></button>
              </div>
            </div>
          </div>
          );
        })}
      </div>

      {filtered.length > LINEUP_PAGE_SIZE && (
        <div className="flex items-center justify-center gap-2 pt-2" data-testid="lineup-pagination">
          <button onClick={() => setPage((p) => Math.max(0, p - 1))} disabled={safePage === 0} data-testid="lineup-prev-page"
            className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/80 text-sm hover:bg-white/10 disabled:opacity-30 inline-flex items-center gap-1"><ChevronLeft className="w-4 h-4"/> Prev</button>
          {Array.from({ length: pageCount }).map((_, i) => (
            <button key={i} onClick={() => setPage(i)} data-testid={`lineup-page-${i + 1}`}
              className={`w-8 h-8 rounded-lg text-sm font-bold transition-colors ${i === safePage ? 'bg-[#E63329] text-white' : 'bg-white/5 text-white/70 hover:bg-white/10'}`}>{i + 1}</button>
          ))}
          <button onClick={() => setPage((p) => Math.min(pageCount - 1, p + 1))} disabled={safePage >= pageCount - 1} data-testid="lineup-next-page"
            className="px-3 py-1.5 rounded-lg bg-white/5 border border-white/10 text-white/80 text-sm hover:bg-white/10 disabled:opacity-30 inline-flex items-center gap-1">Next <ChevronRight className="w-4 h-4"/></button>
        </div>
      )}

      {posEditing && (
        <div className="fixed inset-0 z-50 bg-black/60 flex items-start justify-center overflow-y-auto [&>*]:my-auto p-4" onClick={() => setPosEditing(null)}>
          <div className="bg-white rounded-2xl border-[2.5px] border-[#1a1a1a] shadow-[8px_8px_0_#1a1a1a] max-w-sm w-full p-5" onClick={(ev) => ev.stopPropagation()} data-testid="lineup-position-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="outline-title text-xl inline-flex items-center gap-2"><Move className="w-5 h-5"/> Atur Posisi, Ukuran & Background</h3>
              <button onClick={() => setPosEditing(null)} className="p-1 hover:bg-black/5 rounded"><X className="w-5 h-5"/></button>
            </div>
            <p className="text-[12px] text-black/60 mb-3">Drag gambar untuk menggeser posisi secara bebas · scroll mouse / slider untuk ukuran · Eyedrop untuk ambil warna background.</p>
            <div
              ref={frameRef}
              onPointerDown={(ev) => { if (pickMode) { sampleAt(ev); return; } draggingRef.current = true; ev.currentTarget.setPointerCapture(ev.pointerId); posFromEvent(ev); }}
              onPointerMove={(ev) => { if (draggingRef.current) posFromEvent(ev); }}
              onPointerUp={() => { draggingRef.current = false; dragStartRef.current = null; }}
              className={`aspect-square w-full rounded-xl overflow-hidden border-2 border-[#1a1a1a] relative ${pickMode ? 'cursor-copy' : 'cursor-grab active:cursor-grabbing'} select-none touch-none bg-[#f6f1e7]`} style={{ background: posBg || undefined }}
              data-testid="lineup-position-frame"
            >
              <img ref={posImgRef} src={resolveImageUrl(posEditing.image_url)} alt="" draggable={false} className="w-full h-full object-cover pointer-events-none" style={{ objectPosition: `${pos.x}% ${pos.y}%`, transform: `scale(${posScale})`, transformOrigin: `${pos.x}% ${pos.y}%` }} />
              {showShape && (() => { const si = lineupShapeIndex(posEditing, entries); const sd = LINEUP_SHAPES[si]; const mid = `shpmask-${posEditing.id}`; return (
                <svg viewBox="0 0 100 100" preserveAspectRatio="none" className="absolute inset-0 w-full h-full pointer-events-none" data-testid="lineup-shape-overlay">
                  <defs><mask id={mid}><rect x="0" y="0" width="100" height="100" fill="white" /><path d={sd} fill="black" /></mask></defs>
                  <rect x="0" y="0" width="100" height="100" fill="rgba(0,0,0,0.55)" mask={`url(#${mid})`} />
                  <path d={sd} fill="none" stroke="#111" strokeWidth="3" strokeLinejoin="round" vectorEffect="non-scaling-stroke" />
                  <path d={sd} fill="none" stroke="#CAFF00" strokeWidth="1.5" strokeDasharray="6 4" vectorEffect="non-scaling-stroke" />
                </svg>); })()}
              <div className="hidden absolute w-5 h-5 rounded-full border-2 border-white bg-[#E63329]/80 shadow pointer-events-none -translate-x-1/2 -translate-y-1/2" style={{ left: `${pos.x}%`, top: `${pos.y}%` }} />
            </div>
            <div className="text-[11px] font-mono text-black/50 mt-2">position: {pos.x}% {pos.y}% · ukuran: {Math.round(posScale * 100)}%</div>
            <label className="mt-2 inline-flex items-center gap-2 text-[12px] font-semibold text-black/70" data-testid="lineup-shape-toggle">
              <input type="checkbox" checked={showShape} onChange={(ev) => setShowShape(ev.target.checked)} />
              Tampilkan shape homepage (shape #{lineupShapeIndex(posEditing, entries) + 1}) - area gelap = tidak terlihat di homepage
            </label>
            <div className="mt-3 space-y-3" data-testid="lineup-resize-controls">
              <label className="block">
                <div className="flex justify-between text-[11px] font-black uppercase tracking-widest text-black/70 mb-1"><span>Ukuran gambar</span><span className="font-mono">{Math.round(posScale * 100)}%</span></div>
                <input type="range" min="0.3" max="3" step="0.05" value={posScale} onChange={(ev) => setPosScale(Number(ev.target.value))} className="w-full accent-[#E63329]" data-testid="lineup-scale-slider" />
              </label>
              <div>
                <div className="text-[11px] font-black uppercase tracking-widest text-black/70 mb-1">Background</div>
                <div className="flex items-center gap-2 flex-wrap">
                  <input type="color" value={posBg || '#ECEAE4'} onChange={(ev) => setPosBg(ev.target.value)} className="w-9 h-9 rounded border-2 border-[#1a1a1a] cursor-pointer" data-testid="lineup-bg-picker" />
                  <button type="button" onClick={pickColor} title="Eyedropper: ambil warna dari layar / gambar" data-testid="lineup-eyedropper"
                    className={`h-9 px-2.5 rounded border-2 border-[#1a1a1a] text-[11px] font-bold inline-flex items-center gap-1 ${pickMode ? 'bg-[#CAFF00]' : 'hover:bg-[#f6f1e7]'}`}>
                    <PipetteIcon className="w-4 h-4" /> {pickMode ? 'Klik di gambar...' : 'Eyedrop'}
                  </button>
                  {['#ECEAE4', '#FFFFFF', '#111111', '#CAFF00', '#E63329', '#F5C518', '#8D8B85'].map((c) => (
                    <button key={c} type="button" onClick={() => setPosBg(c)} title={c} className={`w-7 h-7 rounded-full border-2 ${posBg === c ? 'border-[#E63329] scale-110' : 'border-[#1a1a1a]'}`} style={{ background: c }} />
                  ))}
                  <button type="button" onClick={() => setPosBg('')} className="text-[11px] font-semibold underline text-black/60">Default</button>
                </div>
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => { setPos({ x: 50, y: 50 }); setPosScale(1); setPosBg(''); }} className="flex-1 text-sm font-semibold py-2 rounded-lg border-2 border-[#1a1a1a] hover:bg-[#f6f1e7]">Reset</button>
              <button onClick={savePos} disabled={posSaving} data-testid="lineup-position-save"
                className="flex-1 text-sm font-bold py-2 rounded-lg bg-[#E63329] text-white hover:bg-[#c92a21] disabled:opacity-50 inline-flex items-center justify-center gap-1.5">
                {posSaving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} Simpan
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto [&>*]:my-auto p-4" onClick={close}>
          <div className="bg-white rounded-2xl border-[2.5px] border-[#1a1a1a] shadow-[8px_8px_0_#1a1a1a] max-w-md w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(ev) => ev.stopPropagation()} data-testid="lineup-form-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="outline-title text-xl">{editing === 'new' ? 'Tambah Line Up' : 'Edit Line Up'}</h3>
              <button onClick={close} className="p-1 hover:bg-black/5 rounded"><X className="w-5 h-5"/></button>
            </div>

            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-20 h-20 rounded-lg border-2 border-[#1a1a1a] bg-[#f6f1e7] overflow-hidden flex-shrink-0">
                  {form.image_url
                    ? <img src={resolveImageUrl(form.image_url)} alt="" className="w-full h-full object-cover"/>
                    : <div className="w-full h-full flex items-center justify-center text-black/30 text-[10px]">No image</div>}
                </div>
                <label className="flex-1">
                  <div className="text-[11px] font-black uppercase tracking-widest text-black/70 mb-1">Foto / Logo</div>
                  <div className="inline-flex items-center gap-1.5 text-sm font-semibold py-2 px-3 rounded-lg border-2 border-[#1a1a1a] cursor-pointer hover:bg-[#f6f1e7]">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>}
                    {uploading ? 'Mengunggah…' : 'Upload Gambar'}
                  </div>
                  <input type="file" accept="image/*" className="hidden" data-testid="lineup-upload-input"
                    onChange={(ev) => handleUpload(ev.target.files?.[0])}/>
                  <div className="text-[10px] text-black/40 mt-1">JPG / PNG / WEBP • maks 5MB</div>
                </label>
              </div>

              <LField label="Nama" required>
                <input value={form.name} onChange={(ev) => upd('name', ev.target.value)} data-testid="lineup-name-input"
                  className="w-full h-10 px-3 rounded-lg border-2 border-[#1a1a1a] text-sm focus:outline-none focus:ring-2 focus:ring-[#E63329]"/>
              </LField>

              <LField label="Kategori / Peran" hint="mis. Illustration, Sponsor">
                <input value={form.role} onChange={(ev) => upd('role', ev.target.value)} data-testid="lineup-role-input"
                  className="w-full h-10 px-3 rounded-lg border-2 border-[#1a1a1a] text-sm focus:outline-none focus:ring-2 focus:ring-[#E63329]"/>
              </LField>

              <div className="grid grid-cols-2 gap-3">
                <LField label="Tipe">
                  <select value={form.type} onChange={(ev) => upd('type', ev.target.value)} data-testid="lineup-type-select"
                    className="w-full h-10 px-2 rounded-lg border-2 border-[#1a1a1a] text-sm bg-white focus:outline-none focus:ring-2 focus:ring-[#E63329]">
                    <option value="creator">Creator</option>
                    <option value="brand">Brand</option>
                  </select>
                </LField>
                <LField label="Urutan">
                  <input type="number" value={form.order} onChange={(ev) => upd('order', ev.target.value)}
                    className="w-full h-10 px-3 rounded-lg border-2 border-[#1a1a1a] text-sm focus:outline-none focus:ring-2 focus:ring-[#E63329]"/>
                </LField>
              </div>

              <LField label="Link Media Sosial" hint="opsional">
                <input value={form.social_link} onChange={(ev) => upd('social_link', ev.target.value)} placeholder="https://instagram.com/…"
                  className="w-full h-10 px-3 rounded-lg border-2 border-[#1a1a1a] text-sm focus:outline-none focus:ring-2 focus:ring-[#E63329]"/>
              </LField>

              <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(ev) => upd('active', ev.target.checked)} className="w-4 h-4"/>
                Tampilkan di homepage
              </label>

              {error && <div className="text-sm text-red-600 font-semibold">{error}</div>}

              <div className="flex gap-2 pt-1">
                <button onClick={close} className="flex-1 py-2.5 rounded-lg border-2 border-[#1a1a1a] font-semibold text-sm hover:bg-[#f6f1e7]">Batal</button>
                <button onClick={save} disabled={saving || uploading} data-testid="lineup-save-btn"
                  className="flex-1 py-2.5 rounded-lg border-2 border-[#1a1a1a] bg-[#E63329] text-white font-bold text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} Simpan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto [&>*]:my-auto p-4" onClick={() => setDeleteId(null)}>
          <div className="bg-white rounded-2xl border-[2.5px] border-[#1a1a1a] shadow-[8px_8px_0_#1a1a1a] max-w-sm w-full p-5 text-center" onClick={(ev) => ev.stopPropagation()}>
            <Trash2 className="w-10 h-10 text-red-500 mx-auto mb-2"/>
            <div className="font-display text-lg mb-1">Hapus entri ini?</div>
            <p className="text-sm text-black/60 mb-4">Tindakan ini permanen dan tidak bisa dibatalkan.</p>
            <div className="flex gap-2">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 rounded-lg border-2 border-[#1a1a1a] font-semibold text-sm">Batal</button>
              <button onClick={doDelete} data-testid="lineup-confirm-delete" className="flex-1 py-2.5 rounded-lg border-2 border-red-500 bg-red-500 text-white font-bold text-sm">Hapus</button>
            </div>
          </div>
        </div>
      )}

      {bulkOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto [&>*]:my-auto p-4" onClick={() => !bulkSaving && setBulkOpen(false)}>
          <div className="bg-white rounded-2xl border-[2.5px] border-[#1a1a1a] shadow-[8px_8px_0_#1a1a1a] max-w-md w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(ev) => ev.stopPropagation()} data-testid="lineup-bulk-modal">
            <div className="flex items-center justify-between mb-3">
              <h3 className="outline-title text-xl">Bulk Upload Tenant</h3>
              <button onClick={() => setBulkOpen(false)} className="p-1 hover:bg-black/5 rounded"><X className="w-5 h-5"/></button>
            </div>
            <p className="text-[13px] text-black/60 mb-3">Tempel daftar nama tenant — <b>satu nama per baris</b>. Semua akan ditambahkan ke Line Up.</p>
            <div className="mb-3">
              <div className="text-[11px] font-black uppercase tracking-widest text-black/70 mb-1">Tipe</div>
              <div className="flex gap-2">
                {['creator', 'brand'].map((t) => (
                  <button key={t} type="button" onClick={() => setBulkType(t)}
                    className={`flex-1 h-10 rounded-lg border-2 text-sm font-bold capitalize ${bulkType === t ? 'bg-[#E63329] text-white border-[#1a1a1a]' : 'bg-white text-[#1a1a1a] border-[#1a1a1a] hover:bg-[#f6f1e7]'}`}>
                    {t}
                  </button>
                ))}
              </div>
            </div>
            <textarea value={bulkText} onChange={(e) => setBulkText(e.target.value)} rows={12} data-testid="lineup-bulk-textarea"
              placeholder={'Alkalilers\nAmateurs Studio\nAniwayang\n...'}
              className="w-full px-3 py-2.5 rounded-lg border-2 border-[#1a1a1a] text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#E63329] resize-y"/>
            <div className="text-[12px] text-black/50 mt-1">{bulkNames.length} nama terdeteksi</div>
            {bulkErr && <div className="text-sm text-red-600 font-semibold mt-2">{bulkErr}</div>}
            <div className="flex gap-2 pt-3">
              <button onClick={() => setBulkOpen(false)} className="flex-1 py-2.5 rounded-lg border-2 border-[#1a1a1a] font-semibold text-sm hover:bg-[#f6f1e7]">Batal</button>
              <button onClick={submitBulk} disabled={bulkSaving || bulkNames.length === 0} data-testid="lineup-bulk-submit"
                className="flex-1 py-2.5 rounded-lg border-2 border-[#1a1a1a] bg-[#E63329] text-white font-bold text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
                {bulkSaving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>} Tambah {bulkNames.length || ''}
              </button>
            </div>
          </div>
        </div>
      )}

      {deleteAllOpen && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto [&>*]:my-auto p-4" onClick={() => !deletingAll && setDeleteAllOpen(false)}>
          <div className="bg-white rounded-2xl border-[2.5px] border-[#1a1a1a] shadow-[8px_8px_0_#1a1a1a] max-w-sm w-full p-5 text-center" onClick={(ev) => ev.stopPropagation()} data-testid="lineup-delete-all-modal">
            <Trash2 className="w-10 h-10 text-red-500 mx-auto mb-2"/>
            <div className="font-display text-lg mb-1">Hapus SEMUA Line Up?</div>
            <p className="text-sm text-black/60 mb-4">Semua <b>{entries.length}</b> entri akan dihapus permanen. Tindakan ini tidak bisa dibatalkan.</p>
            <div className="flex gap-2">
              <button onClick={() => setDeleteAllOpen(false)} disabled={deletingAll} className="flex-1 py-2.5 rounded-lg border-2 border-[#1a1a1a] font-semibold text-sm">Batal</button>
              <button onClick={doDeleteAll} disabled={deletingAll} data-testid="lineup-confirm-delete-all" className="flex-1 py-2.5 rounded-lg border-2 border-red-500 bg-red-500 text-white font-bold text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
                {deletingAll ? <Loader2 className="w-4 h-4 animate-spin"/> : <Trash2 className="w-4 h-4"/>} Hapus Semua
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function MediaPartnersTab({ entries, onReload }) {
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', logo_url: '', website: '', order: 0, active: true });
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [deleteId, setDeleteId] = useState(null);

  const openNew = () => { setForm({ name: '', logo_url: '', website: '', order: (entries.length ? Math.max(...entries.map(e => e.order || 0)) + 1 : 1), active: true }); setEditing('new'); setError(''); };
  const openEdit = (e) => { setForm({ name: e.name || '', logo_url: e.logo_url || '', website: e.website || '', order: e.order || 0, active: e.active !== false }); setEditing(e); setError(''); };
  const close = () => { setEditing(null); setError(''); };
  const upd = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const handleUpload = async (file) => {
    if (!file) return;
    setUploading(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/media-partners/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      upd('logo_url', data.logo_url);
    } catch (err) { setError(err?.response?.data?.detail || 'Gagal mengunggah logo.'); }
    finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.name.trim()) { setError('Nama wajib diisi.'); return; }
    if (!form.logo_url) { setError('Logo wajib diupload.'); return; }
    setSaving(true); setError('');
    try {
      const payload = { name: form.name, logo_url: form.logo_url, website: form.website, order: Number(form.order) || 0, active: !!form.active };
      if (editing === 'new') await api.post('/media-partners', payload);
      else await api.put(`/media-partners/${editing.id}`, payload);
      close(); onReload();
    } catch (err) { setError(err?.response?.data?.detail || 'Gagal menyimpan.'); }
    finally { setSaving(false); }
  };

  const toggleActive = async (e) => { await api.put(`/media-partners/${e.id}`, { active: !e.active }); onReload(); };
  const doDelete = async () => { if (!deleteId) return; await api.delete(`/media-partners/${deleteId}`); setDeleteId(null); onReload(); };

  return (
    <div className="space-y-5" data-testid="media-partners-tab">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="outline-title text-2xl">Media Partners</h3>
          <p className="text-sm text-black/60">Logo yang tampil di section Media Partners halaman /memories.</p>
        </div>
        <button onClick={openNew} data-testid="mp-add-btn"
          className="bg-[#E63329] text-white px-4 py-2 rounded-lg text-sm font-bold inline-flex items-center gap-1.5 hover:bg-[#c92a21] border-2 border-[#1a1a1a] shadow-[3px_3px_0_#1a1a1a]">
          <Plus className="w-4 h-4"/> Tambah
        </button>
      </div>

      {entries.length === 0 && (
        <div className="bg-white border-2 border-dashed border-black/20 rounded-2xl p-10 text-center text-black/50 text-sm">
          Belum ada media partner. Klik <b>Tambah</b> untuk mengunggah logo pertama.
        </div>
      )}

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {entries.map((e) => (
          <div key={e.id} data-testid={`mp-card-${e.id}`} className={`bg-white border-2 border-[#1a1a1a] rounded-2xl overflow-hidden shadow-[4px_4px_0_#1a1a1a] ${e.active ? '' : 'opacity-60'}`}>
            <div className="h-28 bg-[#f6f1e7] flex items-center justify-center p-4">
              {e.logo_url ? <img src={resolveImageUrl(e.logo_url)} alt={e.name} className="max-h-full max-w-full object-contain"/> : <span className="text-black/30 text-xs">No logo</span>}
            </div>
            <div className="p-3">
              <div className="flex items-center gap-1.5 mb-1">
                {!e.active && <span className="text-[9px] font-black uppercase tracking-wider px-1.5 py-0.5 rounded bg-black/10 text-black/60">hidden</span>}
                <span className="ml-auto text-[10px] text-black/40">#{e.order}</span>
              </div>
              <div className="font-display text-[14px] leading-tight truncate">{e.name}</div>
              {e.website && <div className="text-[11px] text-black/50 truncate">{e.website}</div>}
              <div className="flex items-center gap-1.5 mt-3">
                <button onClick={() => openEdit(e)} data-testid={`mp-edit-${e.id}`} className="flex-1 text-xs font-semibold py-1.5 rounded-md border-2 border-[#1a1a1a] hover:bg-[#f6f1e7] inline-flex items-center justify-center gap-1"><Pencil className="w-3.5 h-3.5"/> Edit</button>
                <button onClick={() => toggleActive(e)} title={e.active ? 'Sembunyikan' : 'Tampilkan'} className="px-2 py-1.5 rounded-md border-2 border-[#1a1a1a] hover:bg-[#f6f1e7]">{e.active ? <Eye className="w-3.5 h-3.5"/> : <EyeOff className="w-3.5 h-3.5"/>}</button>
                <button onClick={() => setDeleteId(e.id)} data-testid={`mp-delete-${e.id}`} className="px-2 py-1.5 rounded-md border-2 border-red-500 text-red-600 hover:bg-red-50"><Trash2 className="w-3.5 h-3.5"/></button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto [&>*]:my-auto p-4" onClick={close}>
          <div className="bg-white rounded-2xl border-[2.5px] border-[#1a1a1a] shadow-[8px_8px_0_#1a1a1a] max-w-md w-full p-5 max-h-[90vh] overflow-y-auto" onClick={(ev) => ev.stopPropagation()} data-testid="mp-form-modal">
            <div className="flex items-center justify-between mb-4">
              <h3 className="outline-title text-xl">{editing === 'new' ? 'Tambah Media Partner' : 'Edit Media Partner'}</h3>
              <button onClick={close} className="p-1 hover:bg-black/5 rounded"><X className="w-5 h-5"/></button>
            </div>
            <div className="space-y-3">
              <div className="flex items-center gap-3">
                <div className="w-24 h-20 rounded-lg border-2 border-[#1a1a1a] bg-[#f6f1e7] overflow-hidden flex items-center justify-center flex-shrink-0 p-2">
                  {form.logo_url ? <img src={resolveImageUrl(form.logo_url)} alt="" className="max-h-full max-w-full object-contain"/> : <span className="text-black/30 text-[10px]">No logo</span>}
                </div>
                <label className="flex-1">
                  <div className="text-[11px] font-black uppercase tracking-widest text-black/70 mb-1">Logo</div>
                  <div className="inline-flex items-center gap-1.5 text-sm font-semibold py-2 px-3 rounded-lg border-2 border-[#1a1a1a] cursor-pointer hover:bg-[#f6f1e7]">
                    {uploading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>}
                    {uploading ? 'Mengunggah…' : 'Upload Logo'}
                  </div>
                  <input type="file" accept="image/*" className="hidden" data-testid="mp-upload-input" onChange={(ev) => handleUpload(ev.target.files?.[0])}/>
                  <div className="text-[10px] text-black/40 mt-1">PNG transparan disarankan • maks 5MB</div>
                </label>
              </div>
              <LField label="Nama Media Partner" required>
                <input value={form.name} onChange={(ev) => upd('name', ev.target.value)} data-testid="mp-name-input"
                  className="w-full h-10 px-3 rounded-lg border-2 border-[#1a1a1a] text-sm focus:outline-none focus:ring-2 focus:ring-[#E63329]"/>
              </LField>
              <div className="grid grid-cols-[1fr_auto] gap-3">
                <LField label="Website / Link" hint="opsional">
                  <input value={form.website} onChange={(ev) => upd('website', ev.target.value)} placeholder="https://…"
                    className="w-full h-10 px-3 rounded-lg border-2 border-[#1a1a1a] text-sm focus:outline-none focus:ring-2 focus:ring-[#E63329]"/>
                </LField>
                <LField label="Urutan">
                  <input type="number" value={form.order} onChange={(ev) => upd('order', ev.target.value)}
                    className="w-20 h-10 px-3 rounded-lg border-2 border-[#1a1a1a] text-sm focus:outline-none focus:ring-2 focus:ring-[#E63329]"/>
                </LField>
              </div>
              <label className="flex items-center gap-2 text-sm font-semibold cursor-pointer">
                <input type="checkbox" checked={form.active} onChange={(ev) => upd('active', ev.target.checked)} className="w-4 h-4"/>
                Tampilkan di halaman /memories
              </label>
              {error && <div className="text-sm text-red-600 font-semibold">{error}</div>}
              <div className="flex gap-2 pt-1">
                <button onClick={close} className="flex-1 py-2.5 rounded-lg border-2 border-[#1a1a1a] font-semibold text-sm hover:bg-[#f6f1e7]">Batal</button>
                <button onClick={save} disabled={saving || uploading} data-testid="mp-save-btn"
                  className="flex-1 py-2.5 rounded-lg border-2 border-[#1a1a1a] bg-[#E63329] text-white font-bold text-sm inline-flex items-center justify-center gap-1.5 disabled:opacity-60">
                  {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} Simpan
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {deleteId && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-start justify-center overflow-y-auto [&>*]:my-auto p-4" onClick={() => setDeleteId(null)}>
          <div className="bg-white rounded-2xl border-[2.5px] border-[#1a1a1a] shadow-[8px_8px_0_#1a1a1a] max-w-sm w-full p-5 text-center" onClick={(ev) => ev.stopPropagation()}>
            <Trash2 className="w-10 h-10 text-red-500 mx-auto mb-2"/>
            <div className="font-display text-lg mb-1">Hapus media partner ini?</div>
            <p className="text-sm text-black/60 mb-4">Tindakan ini permanen.</p>
            <div className="flex gap-2">
              <button onClick={() => setDeleteId(null)} className="flex-1 py-2.5 rounded-lg border-2 border-[#1a1a1a] font-semibold text-sm">Batal</button>
              <button onClick={doDelete} data-testid="mp-confirm-delete" className="flex-1 py-2.5 rounded-lg border-2 border-red-500 bg-red-500 text-white font-bold text-sm">Hapus</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function ProposalTab({ proposal, onSaved }) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [msg, setMsg] = useState('');
  const [deleting, setDeleting] = useState(false);
  const exists = proposal && proposal.exists;

  const fmtSize = (b) => {
    if (!b) return '';
    if (b < 1024 * 1024) return (b / 1024).toFixed(0) + ' KB';
    return (b / 1024 / 1024).toFixed(1) + ' MB';
  };

  const handleUpload = async (file) => {
    if (!file) return;
    if (!file.name.toLowerCase().endsWith('.pdf')) { setError('File harus PDF.'); return; }
    setUploading(true); setError(''); setMsg('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/proposal/upload', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      onSaved({ exists: true, filename: data.filename, size: data.size, uploaded_at: data.uploaded_at, download_url: data.download_url });
      setMsg('Proposal berhasil diunggah!');
      setTimeout(() => setMsg(''), 4000);
    } catch (err) { setError(err?.response?.data?.detail || 'Gagal mengunggah proposal.'); }
    finally { setUploading(false); }
  };

  const doDelete = async () => {
    setDeleting(true);
    try { await api.delete('/proposal'); onSaved({ exists: false }); setMsg('Proposal dihapus.'); setTimeout(() => setMsg(''), 4000); }
    catch (err) { /* ignore */ }
    finally { setDeleting(false); }
  };

  const downloadHref = `${API}/proposal/download`;

  // ---- CTA cards editor ----
  const CTA_META = [
    { key: 'tenant', title: 'Tenant' },
    { key: 'community', title: 'Community' },
    { key: 'fnb', title: 'F&B' },
    { key: 'sponsor', title: 'Sponsor' },
    { key: 'media', title: 'Media Partner' },
  ];
  const [cta, setCta] = useState(null);
  const [ctaSaving, setCtaSaving] = useState(false);
  const [ctaMsg, setCtaMsg] = useState('');

  useEffect(() => {
    api.get('/config/cta').then(({ data }) => setCta(data.items || {})).catch(() => setCta({}));
  }, []);

  const updCta = (key, field, val) => setCta((s) => ({ ...s, [key]: { ...(s[key] || {}), [field]: val } }));

  const saveCta = async () => {
    setCtaSaving(true); setCtaMsg('');
    try {
      const items = {};
      CTA_META.forEach(({ key }) => { const c = cta[key] || {}; items[key] = { desc: c.desc || '', label: c.label || '', link: c.link || '' }; });
      await api.put('/config/cta', { items });
      setCtaMsg('Konten CTA tersimpan! Homepage akan ikut terupdate.');
      setTimeout(() => setCtaMsg(''), 4000);
    } catch (e) { setCtaMsg('Gagal menyimpan CTA.'); }
    finally { setCtaSaving(false); }
  };

  return (
    <div className="space-y-5 max-w-2xl" data-testid="proposal-tab">
      <div>
        <h3 className="outline-title text-2xl">Proposal PDF</h3>
        <p className="text-sm text-black/60">PDF yang di-link pada tombol "Download Proposal" di homepage (Memories Market).</p>
      </div>

      <div className="bg-white border-2 border-[#1a1a1a] rounded-2xl shadow-[4px_4px_0_#1a1a1a] p-5 space-y-4">
        {exists ? (
          <div className="flex items-center gap-4 bg-[#f6f1e7] border-2 border-[#1a1a1a] rounded-xl p-4">
            <div className="w-12 h-14 bg-[#E63329] rounded-md flex items-center justify-center text-white font-black text-[11px] flex-shrink-0">PDF</div>
            <div className="min-w-0 flex-1">
              <div className="font-display text-[15px] truncate" data-testid="proposal-filename">{proposal.filename}</div>
              <div className="text-xs text-black/60">{fmtSize(proposal.size)} • diunggah {proposal.uploaded_at ? parseServerDate(proposal.uploaded_at).toLocaleString() : '-'}</div>
            </div>
            <a href={downloadHref} target="_blank" rel="noreferrer" className="px-3 py-2 rounded-lg border-2 border-[#1a1a1a] text-sm font-bold hover:bg-white inline-flex items-center gap-1.5"><Download className="w-4 h-4"/> Lihat</a>
          </div>
        ) : (
          <div className="bg-[#f6f1e7] border-2 border-dashed border-black/20 rounded-xl p-6 text-center text-sm text-black/50">
            Belum ada proposal. Unggah file PDF di bawah.
          </div>
        )}

        <div className="flex flex-wrap items-center gap-3">
          <label className="inline-flex items-center gap-2 text-sm font-bold py-2.5 px-4 rounded-lg border-2 border-[#1a1a1a] bg-[#E63329] text-white cursor-pointer hover:bg-[#c92a21] shadow-[3px_3px_0_#1a1a1a]">
            {uploading ? <Loader2 className="w-4 h-4 animate-spin"/> : <Upload className="w-4 h-4"/>}
            {uploading ? 'Mengunggah…' : (exists ? 'Ganti Proposal' : 'Upload Proposal')}
            <input type="file" accept="application/pdf,.pdf" className="hidden" data-testid="proposal-upload-input" onChange={(e) => handleUpload(e.target.files?.[0])}/>
          </label>
          {exists && (
            <button onClick={doDelete} disabled={deleting} data-testid="proposal-delete-btn"
              className="inline-flex items-center gap-1.5 text-sm font-bold py-2.5 px-4 rounded-lg border-2 border-red-500 text-red-600 hover:bg-red-50 disabled:opacity-60">
              {deleting ? <Loader2 className="w-4 h-4 animate-spin"/> : <Trash2 className="w-4 h-4"/>} Hapus
            </button>
          )}
          <span className="text-[11px] text-black/40">Format PDF • maks 30MB</span>
        </div>

        {error && <div className="text-sm text-red-600 font-semibold">{error}</div>}
        {msg && <div className="text-sm text-emerald-700 font-semibold" data-testid="proposal-msg">{msg}</div>}
      </div>

      <div className="bg-white border-2 border-[#1a1a1a] rounded-2xl shadow-[4px_4px_0_#1a1a1a] p-5 space-y-4" data-testid="cta-editor">
        <div>
          <h3 className="outline-title text-xl">Konten CTA "Calling All"</h3>
          <p className="text-sm text-black/60">Ubah kalimat konten & link tombol untuk tiap kartu di homepage. Kosongkan untuk pakai default.</p>
        </div>
        {!cta ? (
          <div className="text-sm text-black/50 inline-flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin"/> Memuat…</div>
        ) : (
          <div className="space-y-3">
            {CTA_META.map(({ key, title }) => (
              <div key={key} className="border-2 border-black/15 rounded-xl p-3" data-testid={`cta-row-${key}`}>
                <div className="text-[11px] font-black uppercase tracking-widest text-[#E63329] mb-2">{title}</div>
                <textarea
                  value={(cta[key] && cta[key].desc) || ''}
                  onChange={(e) => updCta(key, 'desc', e.target.value)}
                  rows={2}
                  data-testid={`cta-desc-${key}`}
                  placeholder="Kalimat konten kartu (kosong = default)"
                  className="w-full px-3 py-2 rounded-lg border-2 border-[#1a1a1a] text-sm focus:outline-none focus:ring-2 focus:ring-[#E63329] resize-y mb-2"
                />
                <div className="grid sm:grid-cols-2 gap-2">
                  <input
                    value={(cta[key] && cta[key].label) || ''}
                    onChange={(e) => updCta(key, 'label', e.target.value)}
                    data-testid={`cta-label-${key}`}
                    placeholder="Label tombol (opsional)"
                    className="w-full h-10 px-3 rounded-lg border-2 border-[#1a1a1a] text-sm focus:outline-none focus:ring-2 focus:ring-[#E63329]"
                  />
                  <input
                    value={(cta[key] && cta[key].link) || ''}
                    onChange={(e) => updCta(key, 'link', e.target.value)}
                    data-testid={`cta-link-${key}`}
                    placeholder="Link / URL (mis. /register atau https://wa.me/…)"
                    className="w-full h-10 px-3 rounded-lg border-2 border-[#1a1a1a] text-sm focus:outline-none focus:ring-2 focus:ring-[#E63329]"
                  />
                </div>
              </div>
            ))}
            <div className="flex items-center gap-3">
              <button onClick={saveCta} disabled={ctaSaving} data-testid="cta-save-btn"
                className="bg-[#E63329] text-white px-5 py-2.5 rounded-lg text-sm font-bold inline-flex items-center gap-1.5 hover:bg-[#c92a21] border-2 border-[#1a1a1a] shadow-[3px_3px_0_#1a1a1a] disabled:opacity-60">
                {ctaSaving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} Simpan CTA
              </button>
              {ctaMsg && <span className="text-sm font-semibold text-emerald-700" data-testid="cta-save-msg">{ctaMsg}</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

function MarqueeTab({ items, onSaved }) {
  const [text, setText] = useState((items || []).join('\n'));
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => { setText((items || []).join('\n')); }, [items]);

  const parsed = text.split('\n').map((s) => s.trim()).filter(Boolean);

  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const { data } = await api.put('/config/marquee', { items: parsed });
      onSaved(data.items || []);
      setMsg('Tersimpan! Marquee di homepage akan ikut terupdate.');
    } catch (e) {
      setMsg('Gagal menyimpan: ' + (e?.response?.data?.detail || 'error'));
    } finally { setSaving(false); setTimeout(() => setMsg(''), 4000); }
  };

  return (
    <div className="space-y-5" data-testid="marquee-tab">
      <div>
        <h3 className="outline-title text-2xl">Marquee Homepage</h3>
        <p className="text-sm text-black/60">Teks berjalan di homepage (Memories Market). Satu baris = satu item.</p>
      </div>

      <div className="grid lg:grid-cols-2 gap-5">
        <div className="bg-white border-2 border-[#1a1a1a] rounded-2xl shadow-[4px_4px_0_#1a1a1a] p-4">
          <div className="text-[11px] font-black uppercase tracking-widest text-black/70 mb-2">Daftar Item ({parsed.length})</div>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={16}
            data-testid="marquee-textarea"
            placeholder={'tenant 1\ntenant 2\ntenant 3'}
            className="w-full px-3 py-2.5 rounded-lg border-2 border-[#1a1a1a] text-sm font-medium focus:outline-none focus:ring-2 focus:ring-[#E63329] resize-y"
          />
          <div className="flex items-center gap-3 mt-3">
            <button onClick={save} disabled={saving} data-testid="marquee-save-btn"
              className="bg-[#E63329] text-white px-5 py-2.5 rounded-lg text-sm font-bold inline-flex items-center gap-1.5 hover:bg-[#c92a21] border-2 border-[#1a1a1a] shadow-[3px_3px_0_#1a1a1a] disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin"/> : <Save className="w-4 h-4"/>} Simpan
            </button>
            {msg && <span className="text-sm font-semibold text-emerald-700" data-testid="marquee-save-msg">{msg}</span>}
          </div>
        </div>

        <div className="bg-[#fb8fd3] border-2 border-[#1a1a1a] rounded-2xl shadow-[4px_4px_0_#1a1a1a] p-4 overflow-hidden">
          <div className="text-[11px] font-black uppercase tracking-widest text-white mb-3">Preview</div>
          <div className="overflow-hidden whitespace-nowrap bg-[#fb8fd3] py-3 border-y-2 border-white/40">
            <div className="inline-block animate-[scroll-x_20s_linear_infinite]" style={{ animationName: 'scroll-x' }}>
              {parsed.length === 0 ? (
                <span className="text-white/70 font-bold px-2">(kosong — pakai default)</span>
              ) : (
                [...parsed, ...parsed].map((t, i) => (
                  <span key={i} className="font-display text-white text-2xl px-3">- {t} </span>
                ))
              )}
            </div>
          </div>
          <p className="text-[11px] text-white/90 mt-3 font-medium">Preview hanya ilustrasi. Tampilan asli mengikuti gaya font di homepage.</p>
        </div>
      </div>
    </div>
  );
}

function LField({ label, required, hint, children }) {
  return (
    <label className="block">
      <div className="text-[11px] font-black uppercase tracking-widest text-black/70 mb-1">
        {label} {required && <span className="text-[#E63329]">*</span>}
        {hint && <span className="ml-2 text-[10px] font-medium text-black/40 normal-case tracking-normal">{hint}</span>}
      </div>
      {children}
    </label>
  );
}

function ShortLinksTab() {
  const [items, setItems] = useState([]);
  const [loadingList, setLoadingList] = useState(true);
  const [url, setUrl] = useState('');
  const [slug, setSlug] = useState('');
  const [title, setTitle] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const [copied, setCopied] = useState('');
  const [query, setQuery] = useState('');
  const [editing, setEditing] = useState(null); // { id, url, slug, title }
  const [qrFor, setQrFor] = useState(null);
  const [confirmDel, setConfirmDel] = useState(null);

  const base = `${window.location.origin}/s/`;
  const shortOf = (it) => `${base}${it.slug}`;
  const qrSrc = (text, size = 300) => `https://api.qrserver.com/v1/create-qr-code/?size=${size}x${size}&margin=10&data=${encodeURIComponent(text)}`;
  const errMsg = (e) => e?.response?.data?.detail || 'Terjadi kesalahan, coba lagi.';

  const reload = async () => {
    try { const { data } = await api.get('/shortlinks'); setItems(data); } catch (e) { setErr(errMsg(e)); } finally { setLoadingList(false); }
  };
  useEffect(() => { reload(); }, []);

  const create = async () => {
    if (!url.trim()) return;
    setBusy(true); setErr('');
    try {
      const { data } = await api.post('/shortlinks', { url: url.trim(), slug: slug.trim(), title: title.trim() });
      setItems((prev) => [data, ...prev]);
      setUrl(''); setSlug(''); setTitle('');
      copy(shortOf(data), data.id);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true); setErr('');
    try {
      const { data } = await api.put(`/shortlinks/${editing.id}`, { url: editing.url, slug: editing.slug, title: editing.title });
      setItems((prev) => prev.map((i) => (i.id === data.id ? data : i)));
      setEditing(null);
    } catch (e) { setErr(errMsg(e)); } finally { setBusy(false); }
  };

  const toggleActive = async (it) => {
    try {
      const { data } = await api.put(`/shortlinks/${it.id}`, { active: !(it.active !== false) });
      setItems((prev) => prev.map((i) => (i.id === data.id ? data : i)));
    } catch (e) { setErr(errMsg(e)); }
  };

  const remove = async (it) => {
    try { await api.delete(`/shortlinks/${it.id}`); setItems((prev) => prev.filter((i) => i.id !== it.id)); setConfirmDel(null); } catch (e) { setErr(errMsg(e)); }
  };

  const copy = async (text, id) => {
    try { await navigator.clipboard.writeText(text); } catch (e) { /* ignore */ }
    setCopied(id); setTimeout(() => setCopied(''), 1800);
  };

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((i) => [i.slug, i.url, i.title].some((v) => (v || '').toLowerCase().includes(q)));
  }, [items, query]);

  const totalClicks = items.reduce((a, i) => a + (i.clicks || 0), 0);
  const inputCls = 'w-full bg-[#0A0707] border border-white/10 rounded-xl px-4 py-2.5 text-white placeholder:text-white/30 focus:outline-none focus:border-[#E63329]';

  return (
    <div className="space-y-6" data-testid="shortlinks-tab">
      <div className="tv-card p-6">
        <h3 className="outline-title text-2xl mb-1 inline-flex items-center gap-2"><Link2 className="w-6 h-6 text-[#F5C518]" /> Short URL</h3>
        <p className="text-sm text-white/50 mb-5">Ubah link panjang jadi link pendek <span className="font-mono text-white/70">{base}…</span> dan pantau jumlah kliknya.</p>

        <div className="grid md:grid-cols-[2fr_1fr] gap-3 mb-3">
          <input data-testid="sl-url" value={url} onChange={(e) => setUrl(e.target.value)} onKeyDown={(e) => e.key === 'Enter' && create()}
            placeholder="Tempel URL panjang, mis. https://talesverseworld.com/fnb?utm_source=ig" className={inputCls} />
          <div className="flex items-center bg-[#0A0707] border border-white/10 rounded-xl focus-within:border-[#E63329] overflow-hidden">
            <span className="pl-3 text-white/30 text-sm font-mono whitespace-nowrap">/s/</span>
            <input data-testid="sl-slug" value={slug} onChange={(e) => setSlug(e.target.value.replace(/[^A-Za-z0-9_-]/g, ''))}
              placeholder="slug-custom (opsional)" className="flex-1 bg-transparent px-2 py-2.5 text-white placeholder:text-white/30 focus:outline-none" />
          </div>
        </div>
        <div className="flex flex-col md:flex-row gap-3">
          <input data-testid="sl-title" value={title} onChange={(e) => setTitle(e.target.value)} placeholder="Label / catatan (opsional), mis. Iklan IG F&B"
            className={inputCls} />
          <button data-testid="sl-create" onClick={create} disabled={busy || !url.trim()}
            className="bg-[#E63329] text-white font-bold rounded-xl px-5 py-2.5 text-sm hover:bg-[#F0483E] hover:shadow-[0_0_18px_rgba(230,51,41,0.4)] disabled:opacity-40 inline-flex items-center justify-center gap-2 whitespace-nowrap transition-all">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />} Buat Short URL
          </button>
        </div>
        {err && <div data-testid="sl-error" className="mt-3 text-sm text-red-400 inline-flex items-center gap-2"><AlertCircle className="w-4 h-4" /> {err}</div>}
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div className="tv-card p-4"><div className="text-[10px] uppercase tracking-widest text-white/40">Total link</div><div className="text-2xl font-black text-white">{items.length}</div></div>
        <div className="tv-card p-4"><div className="text-[10px] uppercase tracking-widest text-white/40">Total klik</div><div className="text-2xl font-black text-[#F5C518]">{totalClicks}</div></div>
      </div>

      <div className="tv-card p-6">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <div className="text-[11px] uppercase tracking-widest text-white/40 font-bold">Daftar Short URL ({filtered.length})</div>
          <div className="flex items-center gap-2">
            <div className="relative">
              <Search className="w-4 h-4 text-white/30 absolute left-3 top-1/2 -translate-y-1/2" />
              <input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Cari…" className="bg-[#0A0707] border border-white/10 rounded-xl pl-9 pr-3 py-2 text-sm text-white placeholder:text-white/30 focus:outline-none focus:border-[#E63329]" />
            </div>
            <button onClick={reload} title="Refresh" className="p-2 rounded-xl bg-white/5 border border-white/10 hover:bg-white/10"><RefreshCw className="w-4 h-4" /></button>
          </div>
        </div>

        {loadingList && <div className="text-center py-8 text-white/50"><Loader2 className="w-5 h-5 animate-spin inline" /></div>}
        {!loadingList && filtered.length === 0 && <div className="text-sm text-white/40 py-6 text-center bg-[#0F0A0A] rounded-xl border border-white/5">Belum ada short URL.</div>}

        <div className="space-y-2">
          {filtered.map((it) => {
            const active = it.active !== false;
            const isEdit = editing && editing.id === it.id;
            return (
              <div key={it.id} data-testid={`sl-row-${it.slug}`} className={`bg-[#0F0A0A] border border-white/5 rounded-xl px-4 py-3 ${active ? '' : 'opacity-50'}`}>
                {isEdit ? (
                  <div className="space-y-2">
                    <input value={editing.url} onChange={(e) => setEditing({ ...editing, url: e.target.value })} className={inputCls} placeholder="URL tujuan" />
                    <div className="grid md:grid-cols-2 gap-2">
                      <input value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value.replace(/[^A-Za-z0-9_-]/g, '') })} className={inputCls} placeholder="slug" />
                      <input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })} className={inputCls} placeholder="Label" />
                    </div>
                    <div className="flex gap-2 justify-end">
                      <button onClick={() => setEditing(null)} className="px-4 py-2 text-sm rounded-xl bg-white/5 border border-white/10 hover:bg-white/10">Batal</button>
                      <button onClick={saveEdit} disabled={busy} className="px-4 py-2 text-sm rounded-xl bg-[#E63329] font-bold hover:bg-[#F0483E] inline-flex items-center gap-2">{busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Simpan</button>
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col md:flex-row md:items-center gap-3">
                    <div className="flex-1 min-w-0">
                      {it.title && <div className="text-xs text-white/50 mb-0.5 truncate">{it.title}</div>}
                      <div className="flex items-center gap-2">
                        <a href={shortOf(it)} target="_blank" rel="noreferrer" className="font-mono text-[#F5C518] font-bold truncate hover:underline">{shortOf(it).replace(/^https?:\/\//, '')}</a>
                        {!active && <span className="text-[9px] font-black uppercase px-1.5 py-0.5 rounded bg-white/10 text-white/60">Nonaktif</span>}
                      </div>
                      <div className="text-xs text-white/40 truncate" title={it.url}>→ {it.url}</div>
                    </div>
                    <div className="flex items-center gap-4 shrink-0">
                      <div className="text-right">
                        <div className="text-lg font-black text-white leading-none">{it.clicks || 0}</div>
                        <div className="text-[9px] uppercase tracking-widest text-white/40">klik</div>
                      </div>
                      <div className="text-[10px] text-white/40 hidden lg:block w-24">{it.created_at ? parseServerDate(it.created_at).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }) : ''}</div>
                      <div className="flex items-center gap-1">
                        <button data-testid={`sl-copy-${it.slug}`} onClick={() => copy(shortOf(it), it.id)} title="Copy" className="p-2 rounded-lg hover:bg-white/10 text-white/70">{copied === it.id ? <Check className="w-4 h-4 text-green-400" /> : <Copy className="w-4 h-4" />}</button>
                        <button onClick={() => setQrFor(it)} title="QR Code" className="p-2 rounded-lg hover:bg-white/10 text-white/70"><QrCode className="w-4 h-4" /></button>
                        <a href={it.url} target="_blank" rel="noreferrer" title="Buka URL tujuan" className="p-2 rounded-lg hover:bg-white/10 text-white/70"><ExternalLink className="w-4 h-4" /></a>
                        <button onClick={() => setEditing({ id: it.id, url: it.url, slug: it.slug, title: it.title || '' })} title="Edit" className="p-2 rounded-lg hover:bg-white/10 text-white/70"><Pencil className="w-4 h-4" /></button>
                        <button onClick={() => toggleActive(it)} title={active ? 'Nonaktifkan' : 'Aktifkan'} className="p-2 rounded-lg hover:bg-white/10 text-white/70">{active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}</button>
                        <button onClick={() => setConfirmDel(it)} title="Hapus" className="p-2 rounded-lg hover:bg-white/10 text-white/40 hover:text-red-400"><Trash2 className="w-4 h-4" /></button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>

      {qrFor && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center overflow-y-auto [&>*]:my-auto p-4" onClick={() => setQrFor(null)}>
          <div className="tv-card p-6 w-full max-w-sm text-center" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-lg inline-flex items-center gap-2"><QrCode className="w-5 h-5 text-[#F5C518]" /> QR Code</div>
              <button onClick={() => setQrFor(null)} className="p-1 hover:bg-white/10 rounded"><X className="w-4 h-4" /></button>
            </div>
            <img src={qrSrc(shortOf(qrFor))} alt="QR" className="mx-auto rounded-xl bg-white p-2 w-60 h-60" />
            <div className="font-mono text-sm text-[#F5C518] mt-3 break-all">{shortOf(qrFor).replace(/^https?:\/\//, '')}</div>
            <a href={qrSrc(shortOf(qrFor), 1000)} target="_blank" rel="noreferrer" className="mt-4 inline-flex items-center gap-2 bg-[#E63329] text-white font-bold rounded-xl px-4 py-2.5 text-sm hover:bg-[#F0483E]"><Download className="w-4 h-4" /> Buka QR resolusi tinggi</a>
          </div>
        </div>
      )}

      {confirmDel && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-start justify-center overflow-y-auto [&>*]:my-auto p-4" onClick={() => setConfirmDel(null)}>
          <div className="tv-card p-6 w-full max-w-sm" onClick={(e) => e.stopPropagation()}>
            <div className="font-display text-lg mb-2">Hapus short URL?</div>
            <p className="text-sm text-white/60 mb-5"><span className="font-mono text-[#F5C518]">/s/{confirmDel.slug}</span> akan berhenti berfungsi dan data kliknya hilang.</p>
            <div className="flex justify-end gap-2">
              <button onClick={() => setConfirmDel(null)} className="px-4 py-2 text-sm rounded-xl bg-white/5 border border-white/10 hover:bg-white/10">Batal</button>
              <button data-testid="sl-confirm-delete" onClick={() => remove(confirmDel)} className="px-4 py-2 text-sm rounded-xl bg-red-600 font-bold hover:bg-red-500">Hapus</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


const SOCIAL_PRESETS = [
  { id: 'instagram', label: 'Instagram' },
  { id: 'tiktok', label: 'TikTok' },
  { id: 'whatsapp', label: 'WhatsApp' },
  { id: 'email', label: 'Email' },
  { id: 'youtube', label: 'YouTube' },
  { id: 'x', label: 'X / Twitter' },
  { id: 'facebook', label: 'Facebook' },
];

function SocialsTab() {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(true);
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState('');

  useEffect(() => {
    api.get('/config/socials')
      .then(({ data }) => setItems(data.items || []))
      .catch(() => setItems([]))
      .finally(() => setBusy(false));
  }, []);

  const upd = (i, k, v) => setItems((prev) => prev.map((it, idx) => (idx === i ? { ...it, [k]: v } : it)));
  const remove = (i) => setItems((prev) => prev.filter((_, idx) => idx !== i));
  const add = () => {
    const unused = SOCIAL_PRESETS.filter((p) => !items.some((it) => it.id === p.id));
    const p = unused[0] || { id: `link${items.length + 1}`, label: 'Link' };
    setItems((prev) => [...prev, { id: p.id, label: p.label, url: '', active: true }]);
  };
  const save = async () => {
    setSaving(true); setMsg('');
    try {
      const { data } = await api.put('/config/socials', { items });
      setItems(data.items || []);
      setMsg('Tersimpan! Link social media di homepage ikut terupdate.');
    } catch (e) {
      setMsg('Gagal menyimpan: ' + (e?.response?.data?.detail || 'error'));
    } finally { setSaving(false); setTimeout(() => setMsg(''), 4000); }
  };

  return (
    <div className="space-y-5" data-testid="socials-tab">
      <div>
        <h3 className="outline-title text-2xl">Social Media</h3>
        <p className="text-sm text-black/60">Ganti link Instagram, TikTok, WhatsApp, Email, dll yang tampil di menu homepage. Matikan toggle untuk menyembunyikan.</p>
      </div>
      <div className="bg-white border-2 border-[#1a1a1a] rounded-2xl shadow-[4px_4px_0_#1a1a1a] p-4 space-y-3">
        {busy ? (
          <div className="flex items-center gap-2 text-sm text-black/60"><Loader2 className="w-4 h-4 animate-spin" /> Memuat...</div>
        ) : (
          <>
            {items.length === 0 && <div className="text-sm text-black/50">Belum ada link. Klik Tambah.</div>}
            {items.map((it, i) => (
              <div key={i} className="grid grid-cols-1 md:grid-cols-[150px_150px_1fr_auto_auto] gap-2 items-center" data-testid={`social-row-${it.id}`}>
                <select value={SOCIAL_PRESETS.some((p) => p.id === it.id) ? it.id : 'custom'}
                  onChange={(ev) => { const p = SOCIAL_PRESETS.find((x) => x.id === ev.target.value); if (p) { upd(i, 'id', p.id); upd(i, 'label', p.label); } }}
                  className="px-3 py-2 rounded-lg border-2 border-[#1a1a1a] text-sm font-semibold bg-white">
                  {SOCIAL_PRESETS.map((p) => <option key={p.id} value={p.id}>{p.label}</option>)}
                  {!SOCIAL_PRESETS.some((p) => p.id === it.id) && <option value="custom">{it.id}</option>}
                </select>
                <input value={it.label || ''} onChange={(ev) => upd(i, 'label', ev.target.value)} placeholder="Label"
                  className="px-3 py-2 rounded-lg border-2 border-[#1a1a1a] text-sm" />
                <input value={it.url || ''} onChange={(ev) => upd(i, 'url', ev.target.value)} placeholder="https://... / mailto:... / https://wa.me/62..."
                  className="px-3 py-2 rounded-lg border-2 border-[#1a1a1a] text-sm font-mono" data-testid={`social-url-${it.id}`} />
                <label className="inline-flex items-center gap-1.5 text-xs font-semibold">
                  <input type="checkbox" checked={it.active !== false} onChange={(ev) => upd(i, 'active', ev.target.checked)} /> Tampil
                </label>
                <button type="button" onClick={() => remove(i)} className="p-2 rounded-lg border-2 border-[#1a1a1a] hover:bg-[#f6f1e7]" title="Hapus"><TrashIcon className="w-4 h-4" /></button>
              </div>
            ))}
            <div className="flex items-center gap-3 pt-2 flex-wrap">
              <button type="button" onClick={add} className="px-4 py-2 rounded-lg border-2 border-[#1a1a1a] text-sm font-bold inline-flex items-center gap-1.5 hover:bg-[#f6f1e7]"><PlusIcon className="w-4 h-4" /> Tambah</button>
              <button type="button" onClick={save} disabled={saving} data-testid="socials-save-btn"
                className="bg-[#E63329] text-white px-5 py-2 rounded-lg text-sm font-bold inline-flex items-center gap-1.5 hover:bg-[#c92a21] border-2 border-[#1a1a1a] shadow-[3px_3px_0_#1a1a1a] disabled:opacity-60">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Simpan
              </button>
              {msg && <span className="text-sm font-semibold text-emerald-700">{msg}</span>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}
